/**
 * Cycles in Harmony Glossary & FAQ Meta-Bundle
 * 
 * PROPRIETARY NOTICE: THE ONESCENT DIAL
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * PURPOSE:
 * Centralized glossary and FAQ content for consistent, on-brand messaging
 * throughout the application. All definitions follow CiH terminology and
 * the descriptive, non-predictive interpretation mode.
 * 
 * FRAMEWORK:
 * - Name: Cycles in Harmony (CiH)
 * - Interpretation Mode: Descriptive, non-predictive
 * - Core Cycle: NOURISH → ACTIVATE → FULFILL → INTEGRATE
 * 
 * CATEGORIES:
 * - Orientation, Temporal, Field, Ethics, Method, Metaphysics
 * - Embodiment, Systems, Astrology, Hellenistic, Physics, Wellness
 * - Astronomy, Process, Meaning, Cycle Phase, Symbols
 * 
 * VERSION: 2.0 - Comprehensive Edition (~250 terms)
 */

// =============================================================================
// GLOSSARY CATEGORIES
// =============================================================================

export const GLOSSARY_CATEGORIES = {
  orientation: { label: 'Orientation', color: '#C5A059', description: 'Directional and worldview concepts' },
  temporal: { label: 'Temporal', color: '#4A90E2', description: 'Time-related concepts and cycles' },
  field: { label: 'Field', color: '#7ED321', description: 'Context, place, and conditions' },
  ethics: { label: 'Ethics', color: '#9B59B6', description: 'Boundaries, safety, and responsibility' },
  method: { label: 'Method', color: '#F5A623', description: 'Practices, processes, and approaches' },
  metaphysics: { label: 'Metaphysics', color: '#00F0FF', description: 'Reality structure and meaning' },
  embodiment: { label: 'Embodiment', color: '#E74C3C', description: 'Body, capacity, and lived experience' },
  systems: { label: 'Systems', color: '#1ABC9C', description: 'Relational structures and patterns' },
  astrology: { label: 'Astrology', color: '#FFD700', description: 'Symbolic celestial language' },
  hellenistic: { label: 'Hellenistic', color: '#CD853F', description: 'Classical astrological techniques' },
  physics: { label: 'Physics', color: '#00CED1', description: 'Physical and quantum concepts' },
  wellness: { label: 'Wellness', color: '#FF69B4', description: 'Health, regulation, and embodiment' },
  astronomy: { label: 'Astronomy', color: '#94A3B8', description: 'Observational celestial science' },
  process: { label: 'Process', color: '#E67E22', description: 'Transformation and change' },
  meaning: { label: 'Meaning', color: '#9370DB', description: 'Recognition and realization' },
  cyclePhase: { label: 'Cycle Phase', color: '#3498DB', description: 'Phase-specific concepts' },
  symbols: { label: 'Symbols', color: '#DAA520', description: 'Glyphs and symbolic notation' },
  // NEW CATEGORIES for v2.23
  bodySystem: { label: 'Body Systems', color: '#FF6B6B', description: 'Anatomical correspondences and somatic awareness' },
  archetype: { label: 'Archetype', color: '#9B59B6', description: 'Universal patterns and mythic figures' },
  elementModality: { label: 'Element/Modality', color: '#2ECC71', description: 'Elemental and modal qualities' },
  sabbatTradition: { label: 'Sabbat Traditions', color: '#F39C12', description: 'Seasonal rituals and nature celebrations' },
  placeInfluence: { label: 'Place Influence', color: '#4A9BD9', description: 'Geographic and spatial factors affecting phase expression' }
};

// =============================================================================
// GLOSSARY ENTRIES (~250 terms)
// =============================================================================

export const GLOSSARY = {
  // =========================================================================
  // ORIENTATION (Directional & Worldview)
  // =========================================================================
  north: {
    term: 'NORTH',
    slug: 'north',
    definition: 'A directional reference used for orientation. In Cycles in Harmony, directional labels are descriptive and do not confer hierarchy or superiority.',
    aliases: ['NORTHERN'],
    category: 'orientation',
    relatedTerms: ['hemisphere', 'latitude', 'inversion', 'field'],
    constraints: ['Avoid treating north as default or universal.']
  },
  cosmology: {
    term: 'COSMOLOGY',
    slug: 'cosmology',
    definition: 'A framework of orientation describing how reality is structured and experienced. In Cycles in Harmony, cosmology is descriptive and revisable rather than doctrinal or final.',
    aliases: [],
    category: 'orientation',
    relatedTerms: ['worldview', 'epistemology', 'truth', 'orientation']
  },
  universal: {
    term: 'UNIVERSAL',
    slug: 'universal',
    definition: 'A pattern that repeats across scales. In Cycles in Harmony, the universal is structural (cycle logic) rather than uniform (identical expression).',
    aliases: [],
    category: 'orientation',
    relatedTerms: ['fractal', 'cycle', 'scale']
  },
  fractal: {
    term: 'FRACTAL',
    slug: 'fractal',
    definition: 'A repeating pattern across scales where structure echoes itself in nested forms. In Cycles in Harmony, fractal logic explains why cycles repeat without being identical.',
    aliases: [],
    category: 'orientation',
    relatedTerms: ['universal', 'scale', 'nestedTime']
  },
  orientation: {
    term: 'ORIENTATION',
    slug: 'orientation',
    definition: 'A way of positioning oneself in relationship to conditions, rhythms, and context. In Cycles in Harmony, orientation is dynamic and revisable rather than fixed or final.',
    aliases: ['ORIENT', 'ORIENTING'],
    category: 'orientation',
    relatedTerms: ['cosmology', 'context', 'field', 'attunement']
  },
  attunement: {
    term: 'ATTUNEMENT',
    slug: 'attunement',
    definition: 'The ongoing process of calibrating awareness to present conditions, inner states, and relational context. In Cycles in Harmony, attunement is a practice rather than a destination.',
    aliases: ['ATTUNE', 'ATTUNED'],
    category: 'orientation',
    relatedTerms: ['orientation', 'awareness', 'presence', 'calibration']
  },
  worldview: {
    term: 'WORLDVIEW',
    slug: 'worldview',
    definition: 'The lens through which one perceives and interprets reality. In Cycles in Harmony, worldviews are acknowledged as partial and contextual rather than complete or universal.',
    aliases: [],
    category: 'orientation',
    relatedTerms: ['cosmology', 'perspective', 'orientation']
  },
  perspective: {
    term: 'PERSPECTIVE',
    slug: 'perspective',
    definition: 'A particular vantage point from which reality is observed. In Cycles in Harmony, multiple perspectives coexist without requiring resolution into a single truth.',
    aliases: [],
    category: 'orientation',
    relatedTerms: ['worldview', 'plurality', 'context']
  },

  // =========================================================================
  // TEMPORAL (Time & Cycles)
  // =========================================================================
  time: {
    term: 'TIME',
    slug: 'time',
    definition: 'A layered medium in which cycles unfold. Cycles in Harmony treats time as rhythmic and relational rather than purely linear or purely abstract.',
    aliases: ['TEMPORALITY'],
    category: 'temporal',
    relatedTerms: ['nestedTime', 'phase', 'rhythm', 'continuity']
  },
  temporal: {
    term: 'TEMPORAL',
    slug: 'temporal',
    definition: 'Relating to time, timing, and the unfolding of cycles. In Cycles in Harmony, temporal awareness involves multiple nested rhythms rather than a single clock.',
    aliases: [],
    category: 'temporal',
    relatedTerms: ['time', 'rhythm', 'phase', 'cycle']
  },
  phase: {
    term: 'PHASE',
    slug: 'phase',
    definition: 'A distinct stage within a cycle characterized by particular qualities, emphasis, and available activities. In Cycles in Harmony, phases flow into each other rather than switching abruptly.',
    aliases: ['PHASES'],
    category: 'temporal',
    relatedTerms: ['nourish', 'activate', 'fulfill', 'integrate', 'cycle']
  },
  rhythm: {
    term: 'RHYTHM',
    slug: 'rhythm',
    definition: 'A recurring pattern of timing that shapes experience without forcing it. In Cycles in Harmony, rhythm is felt as much as measured.',
    aliases: ['RHYTHMS'],
    category: 'temporal',
    relatedTerms: ['cycle', 'phase', 'time', 'pace']
  },
  cycle: {
    term: 'CYCLE',
    slug: 'cycle',
    definition: 'A complete pattern of change that returns to a beginning while being transformed. In Cycles in Harmony, cycles nest within cycles at multiple scales.',
    aliases: ['CYCLES'],
    category: 'temporal',
    relatedTerms: ['phase', 'rhythm', 'fractal', 'universal']
  },
  sun: {
    term: 'SUN',
    slug: 'sun',
    definition: 'The luminary governing seasonal orientation through light and dark balance across the year. In Cycles in Harmony, the Sun anchors long-arc identity and continuity themes.',
    aliases: [],
    category: 'temporal',
    glyph: '☉',
    relatedTerms: ['solar', 'season', 'lightBalance', 'luminaries']
  },
  moon: {
    term: 'MOON',
    slug: 'moon',
    definition: 'The luminary governing short-arc embodied rhythm through phases. In Cycles in Harmony, the Moon anchors emotional tides, inner timing, and cyclical recalibration.',
    aliases: [],
    category: 'temporal',
    glyph: '☽',
    relatedTerms: ['lunar', 'phase', 'luminaries']
  },
  luminaries: {
    term: 'LUMINARIES',
    slug: 'luminaries',
    definition: 'The Sun and Moon as primary timing anchors. In Cycles in Harmony, luminaries orient rhythm rather than dictate outcomes.',
    aliases: ['LUMINARY'],
    category: 'temporal',
    relatedTerms: ['sun', 'moon']
  },
  initiation: {
    term: 'INITIATION',
    slug: 'initiation',
    definition: 'A threshold phase where a new cycle begins or a new orientation emerges. In Cycles in Harmony, initiation is often quiet and seedlike.',
    aliases: ['INITIATE'],
    category: 'temporal',
    relatedTerms: ['threshold', 'portal', 'nourish']
  },
  threshold: {
    term: 'THRESHOLD',
    slug: 'threshold',
    definition: 'A boundary point between phases or states where conditions change and new timing becomes available.',
    aliases: [],
    category: 'temporal',
    relatedTerms: ['portal', 'transformation', 'sabbat']
  },
  portal: {
    term: 'PORTAL',
    slug: 'portal',
    definition: 'A symbolic framing for a threshold that emphasizes transition and increased sensitivity to timing and meaning.',
    aliases: [],
    category: 'temporal',
    relatedTerms: ['threshold', 'sabbat', 'eclipse']
  },
  season: {
    term: 'SEASON',
    slug: 'season',
    definition: 'A solar-defined period characterized by light balance, temperature patterns, and ecological rhythms. In Cycles in Harmony, seasons provide long-arc temporal orientation.',
    aliases: ['SEASONS', 'SEASONAL'],
    category: 'temporal',
    relatedTerms: ['sun', 'solstice', 'equinox', 'sabbat']
  },
  solstice: {
    term: 'SOLSTICE',
    slug: 'solstice',
    definition: 'The moment when the Sun reaches its maximum or minimum declination, creating the longest and shortest days. In Cycles in Harmony, solstices mark threshold moments of light-dark extremity.',
    aliases: [],
    category: 'temporal',
    relatedTerms: ['equinox', 'season', 'threshold', 'sabbat']
  },
  equinox: {
    term: 'EQUINOX',
    slug: 'equinox',
    definition: 'The moment when day and night are approximately equal, marking balance points in the solar year. In Cycles in Harmony, equinoxes represent moments of dynamic equilibrium.',
    aliases: [],
    category: 'temporal',
    relatedTerms: ['solstice', 'season', 'balance', 'sabbat']
  },
  sabbat: {
    term: 'SABBAT',
    slug: 'sabbat',
    definition: 'A seasonal marker in the Wheel of the Year tradition, dividing the solar cycle into eight segments. In Cycles in Harmony, sabbats offer cultural interface for seasonal attunement.',
    aliases: ['SABBATS'],
    category: 'temporal',
    relatedTerms: ['season', 'threshold', 'wheelOfYear']
  },

  // =========================================================================
  // FIELD (Context, Place, Conditions)
  // =========================================================================
  field: {
    term: 'FIELD',
    slug: 'field',
    definition: 'The contextual conditions shaping how cycles express in a given time and place. In Cycles in Harmony, the field includes celestial timing, geographic location, cultural context, and embodied conditions.',
    aliases: ['CONDITIONS', 'CLIMATE'],
    category: 'field',
    relatedTerms: ['context', 'space', 'local', 'modulation']
  },
  modulation: {
    term: 'MODULATION',
    slug: 'modulation',
    definition: 'Subtle shifts in emphasis or quality introduced by planetary positions, aspects, or contextual factors. In Cycles in Harmony, modulation colors rather than causes.',
    aliases: ['MODULATE', 'MODULATES'],
    category: 'field',
    relatedTerms: ['planets', 'aspect', 'field', 'emphasis']
  },
  anchor: {
    term: 'ANCHOR',
    slug: 'anchor',
    definition: 'A stabilizing reference point within cyclical change. In Cycles in Harmony, anchors provide continuity while allowing transformation.',
    aliases: ['ANCHORS', 'ANCHORING'],
    category: 'field',
    relatedTerms: ['luminaries', 'orientation', 'stability']
  },
  synthesis: {
    term: 'SYNTHESIS',
    slug: 'synthesis',
    definition: 'The integration of multiple data points into coherent interpretation. In Cycles in Harmony, synthesis describes relationships between layers without reducing them.',
    aliases: ['CIH INTERPRETATION', 'INTERPRETATION'],
    category: 'field',
    relatedTerms: ['orientation', 'context', 'field', 'modulation']
  },
  latitude: {
    term: 'LATITUDE',
    slug: 'latitude',
    definition: 'Angular distance north or south from the equator, fundamentally shaping light balance and seasonal expression. In Cycles in Harmony, latitude determines the local framework for seasonal interpretation.',
    aliases: [],
    category: 'field',
    relatedTerms: ['longitude', 'local', 'inversion', 'space']
  },
  longitude: {
    term: 'LONGITUDE',
    slug: 'longitude',
    definition: 'Angular distance east or west from the prime meridian, affecting time zones and solar timing. In Cycles in Harmony, longitude modulates when daily rhythms peak.',
    aliases: [],
    category: 'field',
    relatedTerms: ['latitude', 'timezone', 'local']
  },
  inversion: {
    term: 'INVERSION',
    slug: 'inversion',
    definition: 'A natural reversal of seasonal or symbolic timing across hemispheres or contexts, where a familiar rhythm expresses in an opposite phase order without being incorrect.',
    aliases: ['SEASONAL INVERSION'],
    category: 'field',
    relatedTerms: ['hemisphere', 'latitude', 'lightBalance', 'field']
  },
  space: {
    term: 'SPACE',
    slug: 'space',
    definition: 'The contextual field in which cycles express. In Cycles in Harmony, space includes place, climate, culture, and conditions shaping how the same cycle is experienced.',
    aliases: ['PLACE'],
    category: 'field',
    relatedTerms: ['field', 'region', 'local', 'global', 'latitude', 'longitude']
  },
  collective: {
    term: 'COLLECTIVE',
    slug: 'collective',
    definition: 'A shared field of relationship among many individuals. In Cycles in Harmony, the collective shapes conditions without erasing personal agency or local context.',
    aliases: [],
    category: 'field',
    relatedTerms: ['individual', 'global', 'local', 'field']
  },
  global: {
    term: 'GLOBAL',
    slug: 'global',
    definition: 'Across the whole world. Cycles in Harmony treats global framing as a broad layer that never overrides local reality.',
    aliases: [],
    category: 'field',
    relatedTerms: ['local', 'region', 'plurality']
  },
  local: {
    term: 'LOCAL',
    slug: 'local',
    definition: 'Place-specific conditions shaping expression of cycles. In Cycles in Harmony, local reality is primary for lived interpretation.',
    aliases: [],
    category: 'field',
    relatedTerms: ['region', 'field', 'context']
  },
  region: {
    term: 'REGION',
    slug: 'region',
    definition: 'A geographically or culturally bounded area with coherent patterns of climate, light, ecology, and meaning-making. In Cycles in Harmony, regions are interfaces for cyclical expression.',
    aliases: ['BIOREGION'],
    category: 'field',
    relatedTerms: ['local', 'global', 'field']
  },
  context: {
    term: 'CONTEXT',
    slug: 'context',
    definition: 'The surrounding conditions that shape meaning and expression. In Cycles in Harmony, context is always acknowledged as partial and influential.',
    aliases: [],
    category: 'field',
    relatedTerms: ['field', 'local', 'modulation']
  },
  hemisphere: {
    term: 'HEMISPHERE',
    slug: 'hemisphere',
    definition: 'Half of the Earth divided by the equator (north/south) or prime meridian (east/west). In Cycles in Harmony, hemispheric location fundamentally shapes seasonal timing.',
    aliases: [],
    category: 'field',
    relatedTerms: ['latitude', 'inversion', 'season']
  },
  climate: {
    term: 'CLIMATE',
    slug: 'climate',
    definition: 'Long-term atmospheric patterns of a region. In Cycles in Harmony, climate shapes how universal cycles express locally—tropical, temperate, polar, arid each have distinct rhythms.',
    aliases: [],
    category: 'field',
    relatedTerms: ['field', 'local', 'season', 'weather']
  },
  placeSpecific: {
    term: 'PLACE-SPECIFIC',
    slug: 'place-specific',
    definition: 'Unique conditions created by geographic location that modulate how cosmic rhythms manifest. Latitude, hemisphere, daylight hours, and altitude all contribute to place-specific influence.',
    aliases: ['PLACE INFLUENCE', 'GEOGRAPHIC INFLUENCE'],
    category: 'placeInfluence',
    relatedTerms: ['hemisphere', 'latitude', 'daylight', 'spatial']
  },
  spacioTemporal: {
    term: 'SPACIO-TEMPORAL',
    slug: 'spacio-temporal',
    definition: 'The integration of spatial (place) and temporal (time) factors. The Spacio-Temporal Synthesis shows how your geographic conditions are acted upon by cosmic modulators to produce your dominant phase expression.',
    aliases: ['SPACE-TIME'],
    category: 'placeInfluence',
    relatedTerms: ['placeSpecific', 'phase', 'synthesis', 'orientation']
  },
  solarDominant: {
    term: 'SOLAR DOMINANT',
    slug: 'solar-dominant',
    definition: 'A daylight condition where more hours are illuminated than dark. Solar dominance amplifies ACTIVATE and FULFILL phase tendencies toward expression, action, and manifestation.',
    aliases: ['SUN DOMINANT'],
    category: 'placeInfluence',
    relatedTerms: ['daylight', 'lunarDominant', 'activate', 'fulfill']
  },
  lunarDominant: {
    term: 'LUNAR DOMINANT',
    slug: 'lunar-dominant',
    definition: 'A darkness condition where more hours are unlit than illuminated. Lunar dominance amplifies NOURISH and INTEGRATE phase tendencies toward rest, reflection, and assimilation.',
    aliases: ['MOON DOMINANT'],
    category: 'placeInfluence',
    relatedTerms: ['darkness', 'solarDominant', 'nourish', 'integrate']
  },
  latitudeEffect: {
    term: 'LATITUDE EFFECT',
    slug: 'latitude-effect',
    definition: 'How distance from the equator affects seasonal light variation. Higher latitudes experience more extreme daylight cycles (midnight sun to polar night), amplifying phase emphasis.',
    aliases: [],
    category: 'placeInfluence',
    relatedTerms: ['latitude', 'hemisphere', 'daylight', 'season']
  },

  // =========================================================================
  // ETHICS (Boundaries, Safety, Responsibility)
  // =========================================================================
  plurality: {
    term: 'PLURALITY',
    slug: 'plurality',
    definition: 'The coexistence of multiple valid perspectives or expressions. In Cycles in Harmony, plurality is the norm rather than the exception.',
    aliases: [],
    category: 'ethics',
    relatedTerms: ['diversity', 'context']
  },
  doctrine: {
    term: 'DOCTRINE',
    slug: 'doctrine',
    definition: 'A fixed body of teaching held as authoritative. Cycles in Harmony explicitly avoids doctrinal claims and emphasizes revisability.',
    aliases: [],
    category: 'ethics',
    relatedTerms: ['dogma', 'boundaries', 'orientation'],
    constraints: ['Cycles in Harmony is not doctrine.']
  },
  dogma: {
    term: 'DOGMA',
    slug: 'dogma',
    definition: 'Rigidly held belief treated as unquestionable truth. Cycles in Harmony rejects dogmatic framing in favor of inquiry and revision.',
    aliases: [],
    category: 'ethics',
    relatedTerms: ['doctrine', 'inquiry'],
    constraints: ['Cycles in Harmony is not dogma.']
  },
  diversity: {
    term: 'DIVERSITY',
    slug: 'diversity',
    definition: 'Variation in expression, form, or perspective. In Cycles in Harmony, diversity is inherent to how cycles unfold across different contexts.',
    aliases: [],
    category: 'ethics',
    relatedTerms: ['plurality', 'context', 'local']
  },
  boundaries: {
    term: 'BOUNDARIES',
    slug: 'boundaries',
    definition: 'Limits that define where one thing ends and another begins. In Cycles in Harmony, boundaries are relational and dynamic rather than rigid walls.',
    aliases: ['BOUNDARY'],
    category: 'ethics',
    relatedTerms: ['edges', 'container', 'safety']
  },
  sensitivity: {
    term: 'SENSITIVITY',
    slug: 'sensitivity',
    definition: 'Heightened awareness and responsiveness to stimuli or conditions. In Cycles in Harmony, sensitivity is a feature that requires care rather than a flaw to fix.',
    aliases: [],
    category: 'ethics',
    relatedTerms: ['attunement', 'awareness', 'capacity']
  },
  consent: {
    term: 'CONSENT',
    slug: 'consent',
    definition: 'Active, informed agreement to participate. In Cycles in Harmony applications, consent is fundamental to ethical engagement with personal data and interpretation.',
    aliases: [],
    category: 'ethics',
    relatedTerms: ['boundaries', 'agency', 'safety']
  },
  agency: {
    term: 'AGENCY',
    slug: 'agency',
    definition: 'The capacity to act, choose, and influence one\'s own experience. In Cycles in Harmony, agency is always preserved—conditions inform but do not control.',
    aliases: [],
    category: 'ethics',
    relatedTerms: ['choice', 'autonomy', 'consent']
  },
  safety: {
    term: 'SAFETY',
    slug: 'safety',
    definition: 'A condition of protection from harm. In Cycles in Harmony, safety includes emotional, relational, and interpretive dimensions—not just physical security.',
    aliases: [],
    category: 'ethics',
    relatedTerms: ['boundaries', 'consent', 'container']
  },

  // =========================================================================
  // METHOD (Practices, Processes, Approaches)
  // =========================================================================
  force: {
    term: 'FORCE',
    slug: 'force',
    definition: 'Coercive pressure applied to achieve an outcome. In Cycles in Harmony, force is generally less aligned than invitation or timing.',
    aliases: [],
    category: 'method',
    relatedTerms: ['resistance', 'flow', 'timing']
  },
  pace: {
    term: 'PACE',
    slug: 'pace',
    definition: 'The speed and rhythm of movement or process. In Cycles in Harmony, pace is adaptive rather than fixed.',
    aliases: [],
    category: 'method',
    relatedTerms: ['rhythm', 'timing', 'capacity']
  },
  dynamic: {
    term: 'DYNAMIC',
    slug: 'dynamic',
    definition: 'Characterized by change and movement. In Cycles in Harmony, conditions are dynamic rather than static.',
    aliases: ['DYNAMICS'],
    category: 'method',
    relatedTerms: ['flow', 'process', 'change']
  },
  expression: {
    term: 'EXPRESSION',
    slug: 'expression',
    definition: 'The outward manifestation of inner states or patterns. In Cycles in Harmony, expression varies based on context and phase.',
    aliases: [],
    category: 'method',
    relatedTerms: ['creativity', 'activate', 'fulfill']
  },
  reflection: {
    term: 'REFLECTION',
    slug: 'reflection',
    definition: 'The practice of looking back to understand. In Cycles in Harmony, reflection is an integrate phase activity.',
    aliases: [],
    category: 'method',
    relatedTerms: ['integrate', 'awareness', 'insight']
  },
  attention: {
    term: 'ATTENTION',
    slug: 'attention',
    definition: 'Directed awareness toward something. In Cycles in Harmony, attention is a limited resource that benefits from intentional allocation.',
    aliases: [],
    category: 'method',
    relatedTerms: ['focus', 'awareness', 'presence']
  },
  focus: {
    term: 'FOCUS',
    slug: 'focus',
    definition: 'Concentrated attention on a specific area. In Cycles in Harmony, focus is phase-dependent and context-sensitive.',
    aliases: [],
    category: 'method',
    relatedTerms: ['attention', 'clarity', 'activate']
  },
  practice: {
    term: 'PRACTICE',
    slug: 'practice',
    definition: 'Repeated engagement with a skill or awareness pattern. In Cycles in Harmony, practice builds capacity over time.',
    aliases: [],
    category: 'method',
    relatedTerms: ['attunement', 'habit', 'rhythm']
  },
  practitioner: {
    term: 'PRACTITIONER',
    slug: 'practitioner',
    definition: 'One who engages in a practice. In Cycles in Harmony, all users are practitioners of their own attunement.',
    aliases: [],
    category: 'method',
    relatedTerms: ['practice', 'agency']
  },
  calibration: {
    term: 'CALIBRATION',
    slug: 'calibration',
    definition: 'The process of fine-tuning or adjusting for accuracy. In Cycles in Harmony, calibration is ongoing—attunement requires regular recalibration.',
    aliases: [],
    category: 'method',
    relatedTerms: ['attunement', 'adjustment', 'refinement']
  },
  inquiry: {
    term: 'INQUIRY',
    slug: 'inquiry',
    definition: 'The practice of asking questions and exploring. In Cycles in Harmony, inquiry is preferred over assertion—we ask rather than declare.',
    aliases: [],
    category: 'method',
    relatedTerms: ['curiosity', 'exploration', 'learning']
  },

  // =========================================================================
  // METAPHYSICS (Reality Structure & Meaning)
  // =========================================================================
  resistance: {
    term: 'RESISTANCE',
    slug: 'resistance',
    definition: 'Friction or opposition to movement or change. In Cycles in Harmony, resistance often signals misalignment with timing or capacity.',
    aliases: [],
    category: 'metaphysics',
    relatedTerms: ['flow', 'friction', 'timing']
  },
  flow: {
    term: 'FLOW',
    slug: 'flow',
    definition: 'Unimpeded movement or process. In Cycles in Harmony, flow emerges from alignment rather than force.',
    aliases: ['FLOWING'],
    category: 'metaphysics',
    relatedTerms: ['ease', 'rhythm', 'resistance']
  },
  ease: {
    term: 'EASE',
    slug: 'ease',
    definition: 'The quality of movement without unnecessary struggle. In Cycles in Harmony, ease is a signal of alignment with timing and capacity.',
    aliases: [],
    category: 'metaphysics',
    relatedTerms: ['flow', 'grace', 'alignment']
  },
  curiosity: {
    term: 'CURIOSITY',
    slug: 'curiosity',
    definition: 'Open interest without attachment to outcome. In Cycles in Harmony, curiosity is a foundational orientation for inquiry.',
    aliases: [],
    category: 'metaphysics',
    relatedTerms: ['inquiry', 'openness', 'learning']
  },
  creativity: {
    term: 'CREATIVITY',
    slug: 'creativity',
    definition: 'The capacity to generate new forms, ideas, or connections. In Cycles in Harmony, creativity is phase-influenced and context-shaped.',
    aliases: [],
    category: 'metaphysics',
    relatedTerms: ['expression', 'activate', 'fulfill']
  },
  feedback: {
    term: 'FEEDBACK',
    slug: 'feedback',
    definition: 'Information returned from action that informs adjustment. In Cycles in Harmony, feedback loops are how systems learn and adapt.',
    aliases: [],
    category: 'metaphysics',
    relatedTerms: ['calibration', 'learning', 'adaptation']
  },
  release: {
    term: 'RELEASE',
    slug: 'release',
    definition: 'Letting go of what no longer serves. In Cycles in Harmony, release is an integrate phase activity.',
    aliases: [],
    category: 'metaphysics',
    relatedTerms: ['integrate', 'letting go', 'completion']
  },
  transformation: {
    term: 'TRANSFORMATION',
    slug: 'transformation',
    definition: 'Fundamental change in form or nature. In Cycles in Harmony, transformation occurs across cycles and is never fully complete.',
    aliases: [],
    category: 'metaphysics',
    relatedTerms: ['change', 'process', 'evolution']
  },
  duality: {
    term: 'DUALITY',
    slug: 'duality',
    definition: 'A two-part structure of apparent opposites. In Cycles in Harmony, dualities are relational tensions rather than fixed categories.',
    aliases: ['POLARITY'],
    category: 'metaphysics',
    relatedTerms: ['non-duality', 'polarity', 'relationship']
  },
  nonDuality: {
    term: 'NON-DUALITY',
    slug: 'non-duality',
    definition: 'A framing in which apparent opposites are understood as continuous expressions of one field of reality. In Cycles in Harmony, non-duality coexists with practical dualities without canceling them.',
    aliases: ['NONDUALITY'],
    category: 'metaphysics',
    relatedTerms: ['duality', 'polarity', 'relationship']
  },
  crystallineWeb: {
    term: 'CRYSTALLINE WEB',
    slug: 'crystalline-web',
    definition: 'A Cycles in Harmony visualization metaphor describing reality as a network of relational nodes where shared structure refracts into diverse expression across context and scale.',
    aliases: ['WEB', 'RELATIONAL WEB'],
    category: 'metaphysics',
    relatedTerms: ['node', 'refraction', 'relationship', 'field', 'layeredReality'],
    constraints: ['This is a metaphor for orientation, not a literal scientific claim.']
  },
  emergence: {
    term: 'EMERGENCE',
    slug: 'emergence',
    definition: 'The arising of new patterns or properties from the interaction of simpler elements. In Cycles in Harmony, meaning emerges rather than being imposed.',
    aliases: ['EMERGENT'],
    category: 'metaphysics',
    relatedTerms: ['complexity', 'pattern', 'synthesis']
  },
  resonance: {
    term: 'RESONANCE',
    slug: 'resonance',
    definition: 'A sympathetic vibration or alignment between elements. In Cycles in Harmony, resonance describes when two patterns amplify or support each other.',
    aliases: [],
    category: 'metaphysics',
    relatedTerms: ['harmony', 'alignment', 'aspect']
  },
  harmony: {
    term: 'HARMONY',
    slug: 'harmony',
    definition: 'A pleasing arrangement of parts into a coherent whole. In Cycles in Harmony, harmony is dynamic balance rather than static perfection.',
    aliases: [],
    category: 'metaphysics',
    relatedTerms: ['resonance', 'balance', 'integration']
  },

  // =========================================================================
  // EMBODIMENT (Body, Capacity, Lived Experience)
  // =========================================================================
  individual: {
    term: 'INDIVIDUAL',
    slug: 'individual',
    definition: 'A distinct embodied node within the relational web. In Cycles in Harmony, individuality is real and contextual, not separate from interdependence.',
    aliases: [],
    category: 'embodiment',
    relatedTerms: ['collective', 'node', 'agency']
  },
  intuition: {
    term: 'INTUITION',
    slug: 'intuition',
    definition: 'Embodied knowing that arises through pattern recognition, perception, and nervous system intelligence. In Cycles in Harmony, intuition is a valid input that benefits from calibration.',
    aliases: [],
    category: 'embodiment',
    relatedTerms: ['attunement', 'awareness', 'calibration']
  },
  capacity: {
    term: 'CAPACITY',
    slug: 'capacity',
    definition: 'Available physical, emotional, cognitive, and relational bandwidth. In Cycles in Harmony, capacity modulates what is sustainable in any phase.',
    aliases: [],
    category: 'embodiment',
    relatedTerms: ['nervousSystem', 'pace', 'rest', 'readiness']
  },
  rest: {
    term: 'REST',
    slug: 'rest',
    definition: 'Phase-appropriate reduction of output and stimulation that supports repair, integration, and capacity restoration.',
    aliases: [],
    category: 'embodiment',
    relatedTerms: ['pause', 'nourish', 'recovery']
  },
  pause: {
    term: 'PAUSE',
    slug: 'pause',
    definition: 'A deliberate or natural stop that allows recalibration. In Cycles in Harmony, pause is often an aligned response rather than a delay to fix.',
    aliases: [],
    category: 'embodiment',
    relatedTerms: ['rest', 'attunement', 'timing']
  },
  presence: {
    term: 'PRESENCE',
    slug: 'presence',
    definition: 'Full awareness in the current moment. In Cycles in Harmony, presence is cultivated through practice and supports attunement.',
    aliases: [],
    category: 'embodiment',
    relatedTerms: ['awareness', 'attention', 'grounding']
  },
  grounding: {
    term: 'GROUNDING',
    slug: 'grounding',
    definition: 'The practice of establishing connection with the body and present moment. In Cycles in Harmony, grounding supports embodied orientation.',
    aliases: [],
    category: 'embodiment',
    relatedTerms: ['presence', 'embodiment', 'stability']
  },
  embodiment: {
    term: 'EMBODIMENT',
    slug: 'embodiment',
    definition: 'The lived experience of having a body. In Cycles in Harmony, embodiment is the medium through which all cycles are experienced.',
    aliases: [],
    category: 'embodiment',
    relatedTerms: ['body', 'presence', 'capacity']
  },
  body: {
    term: 'BODY',
    slug: 'body',
    definition: 'The physical form through which experience occurs. In Cycles in Harmony, the body is intelligent, communicative, and central to orientation.',
    aliases: [],
    category: 'embodiment',
    relatedTerms: ['embodiment', 'soma', 'nervousSystem']
  },

  // =========================================================================
  // WELLNESS (Health, Regulation, Nervous System)
  // =========================================================================
  nervousSystem: {
    term: 'NERVOUS SYSTEM',
    slug: 'nervous-system',
    definition: 'The biological network governing sensation, response, and regulation. In Cycles in Harmony, nervous system state profoundly influences how cycles are experienced.',
    aliases: ['NS', 'ANS'],
    category: 'wellness',
    relatedTerms: ['regulation', 'soma', 'capacity', 'vagal']
  },
  regulation: {
    term: 'REGULATION',
    slug: 'regulation',
    definition: 'The capacity to maintain or return to a balanced state. In Cycles in Harmony, regulation is a resource that can be depleted or restored.',
    aliases: ['SELF-REGULATION'],
    category: 'wellness',
    relatedTerms: ['nervousSystem', 'dysregulation', 'coregulation']
  },
  dysregulation: {
    term: 'DYSREGULATION',
    slug: 'dysregulation',
    definition: 'A state of nervous system overwhelm where regulation capacity is exceeded. In Cycles in Harmony, dysregulation signals need for support, not failure.',
    aliases: [],
    category: 'wellness',
    relatedTerms: ['regulation', 'nervousSystem', 'capacity']
  },
  coregulation: {
    term: 'CO-REGULATION',
    slug: 'coregulation',
    definition: 'The mutual influence of nervous systems in relationship. In Cycles in Harmony, co-regulation acknowledges we regulate together, not just alone.',
    aliases: ['COREGULATION'],
    category: 'wellness',
    relatedTerms: ['regulation', 'relationship', 'nervousSystem']
  },
  somatic: {
    term: 'SOMATIC',
    slug: 'somatic',
    definition: 'Relating to the body as experienced from within. In Cycles in Harmony, somatic awareness is a primary channel of attunement.',
    aliases: ['SOMA'],
    category: 'wellness',
    relatedTerms: ['body', 'embodiment', 'felt sense']
  },
  feltSense: {
    term: 'FELT SENSE',
    slug: 'felt-sense',
    definition: 'The bodily experience of a situation or state before it is fully articulated. In Cycles in Harmony, felt sense is a source of wisdom.',
    aliases: [],
    category: 'wellness',
    relatedTerms: ['somatic', 'intuition', 'body']
  },
  vagal: {
    term: 'VAGAL',
    slug: 'vagal',
    definition: 'Relating to the vagus nerve, which governs parasympathetic (rest-digest) function. In Cycles in Harmony, vagal tone influences capacity for presence and attunement.',
    aliases: ['VAGUS'],
    category: 'wellness',
    relatedTerms: ['nervousSystem', 'regulation', 'polyvagal']
  },
  polyvagal: {
    term: 'POLYVAGAL',
    slug: 'polyvagal',
    definition: 'A theory describing the nervous system\'s hierarchical responses to safety and threat. In Cycles in Harmony, polyvagal awareness informs compassionate interpretation of state.',
    aliases: [],
    category: 'wellness',
    relatedTerms: ['vagal', 'nervousSystem', 'safety']
  },
  windowOfTolerance: {
    term: 'WINDOW OF TOLERANCE',
    slug: 'window-of-tolerance',
    definition: 'The range of arousal within which one can function effectively. In Cycles in Harmony, capacity and phase interact to shape this window.',
    aliases: [],
    category: 'wellness',
    relatedTerms: ['regulation', 'capacity', 'nervousSystem']
  },
  trauma: {
    term: 'TRAUMA',
    slug: 'trauma',
    definition: 'Overwhelming experiences that exceed the nervous system\'s capacity to integrate. In Cycles in Harmony, trauma awareness informs sensitive, non-prescriptive interpretation.',
    aliases: [],
    category: 'wellness',
    relatedTerms: ['nervousSystem', 'regulation', 'safety'],
    constraints: ['Cycles in Harmony is not therapy and does not diagnose or treat trauma.']
  },
  resilience: {
    term: 'RESILIENCE',
    slug: 'resilience',
    definition: 'The capacity to recover and adapt after challenge. In Cycles in Harmony, resilience is cultivated through appropriate pacing and support.',
    aliases: [],
    category: 'wellness',
    relatedTerms: ['capacity', 'adaptation', 'recovery']
  },
  integration: {
    term: 'INTEGRATION',
    slug: 'integration',
    definition: 'The process of bringing together disparate elements into coherent wholeness. In Cycles in Harmony, integration is ongoing work, not a final achievement.',
    aliases: [],
    category: 'wellness',
    relatedTerms: ['integrate', 'wholeness', 'synthesis']
  },

  // =========================================================================
  // ASTROLOGY (Symbolic Celestial Language)
  // =========================================================================
  astrology: {
    term: 'ASTROLOGY',
    slug: 'astrology',
    definition: 'A symbolic language describing archetypal modulation through planetary and luminary cycles. In Cycles in Harmony, astrology is used descriptively (conditions and emphasis) rather than predictively (outcomes).',
    aliases: [],
    category: 'astrology',
    relatedTerms: ['planets', 'luminaries', 'aspect', 'transit', 'zodiac'],
    constraints: ['Avoid deterministic claims.']
  },
  planets: {
    term: 'PLANETS',
    slug: 'planets',
    definition: 'Celestial bodies used in Cycles in Harmony as archetypal field modulators that shape emphasis and timing without causing events.',
    aliases: ['PLANET'],
    category: 'astrology',
    glyph: '☿♀♂♃♄♅♆♇',
    relatedTerms: ['modulation', 'transit', 'aspect'],
    constraints: ['Avoid causal language.']
  },
  zodiac: {
    term: 'ZODIAC',
    slug: 'zodiac',
    definition: 'The twelve-fold division of the ecliptic into signs, each with archetypal qualities. In Cycles in Harmony, the zodiac is a timing and emphasis framework.',
    aliases: ['ZODIACAL'],
    category: 'astrology',
    relatedTerms: ['sign', 'element', 'modality']
  },
  sign: {
    term: 'SIGN',
    slug: 'sign',
    definition: 'One of twelve zodiacal divisions, each with distinct archetypal qualities. In Cycles in Harmony, signs describe emphasis and style, not personality or fate.',
    aliases: ['ZODIAC SIGN'],
    category: 'astrology',
    relatedTerms: ['zodiac', 'element', 'modality']
  },
  
  // Elements (Four Classical Elements)
  fire: {
    term: 'FIRE',
    slug: 'fire',
    definition: 'One of four classical elements representing initiative, action, and vital energy. Fire signs (Aries, Leo, Sagittarius) emphasize dynamic expression and inspiration.',
    aliases: ['FIRE ELEMENT', 'FIRE SIGNS'],
    category: 'astrology',
    relatedTerms: ['element', 'aries', 'leo', 'sagittarius']
  },
  earth: {
    term: 'EARTH',
    slug: 'earth',
    definition: 'One of four classical elements representing stability, form, and material grounding. Earth signs (Taurus, Virgo, Capricorn) emphasize practical manifestation and endurance.',
    aliases: ['EARTH ELEMENT', 'EARTH SIGNS'],
    category: 'astrology',
    relatedTerms: ['element', 'taurus', 'virgo', 'capricorn']
  },
  air: {
    term: 'AIR',
    slug: 'air',
    definition: 'One of four classical elements representing intellect, communication, and connection. Air signs (Gemini, Libra, Aquarius) emphasize mental exchange and social awareness.',
    aliases: ['AIR ELEMENT', 'AIR SIGNS'],
    category: 'astrology',
    relatedTerms: ['element', 'gemini', 'libra', 'aquarius']
  },
  water: {
    term: 'WATER',
    slug: 'water',
    definition: 'One of four classical elements representing emotion, intuition, and depth. Water signs (Cancer, Scorpio, Pisces) emphasize feeling states and inner perception.',
    aliases: ['WATER ELEMENT', 'WATER SIGNS'],
    category: 'astrology',
    relatedTerms: ['element', 'cancer', 'scorpio', 'pisces']
  },
  element: {
    term: 'ELEMENT',
    slug: 'element',
    definition: 'One of four classical qualities (Fire, Earth, Air, Water) that describe the fundamental nature of zodiacal energy. Elements indicate the mode of expression.',
    aliases: ['ELEMENTS', 'CLASSICAL ELEMENT'],
    category: 'astrology',
    relatedTerms: ['fire', 'earth', 'air', 'water', 'triplicity']
  },
  
  // Modalities (Three Modes of Action)
  cardinal: {
    term: 'CARDINAL',
    slug: 'cardinal',
    definition: 'One of three modalities representing initiation and new beginnings. Cardinal signs (Aries, Cancer, Libra, Capricorn) mark seasonal turning points and emphasize starting energy.',
    aliases: ['CARDINAL MODALITY', 'CARDINAL SIGNS'],
    category: 'astrology',
    relatedTerms: ['modality', 'aries', 'cancer', 'libra', 'capricorn', 'equinox', 'solstice']
  },
  fixed: {
    term: 'FIXED',
    slug: 'fixed',
    definition: 'One of three modalities representing stabilization and persistence. Fixed signs (Taurus, Leo, Scorpio, Aquarius) mark seasonal peaks and emphasize sustaining energy.',
    aliases: ['FIXED MODALITY', 'FIXED SIGNS'],
    category: 'astrology',
    relatedTerms: ['modality', 'taurus', 'leo', 'scorpio', 'aquarius']
  },
  mutable: {
    term: 'MUTABLE',
    slug: 'mutable',
    definition: 'One of three modalities representing adaptation and transition. Mutable signs (Gemini, Virgo, Sagittarius, Pisces) mark seasonal endings and emphasize changing energy.',
    aliases: ['MUTABLE MODALITY', 'MUTABLE SIGNS'],
    category: 'astrology',
    relatedTerms: ['modality', 'gemini', 'virgo', 'sagittarius', 'pisces']
  },
  modality: {
    term: 'MODALITY',
    slug: 'modality',
    definition: 'One of three qualities (Cardinal, Fixed, Mutable) that describe the mode of action for zodiacal energy. Modality indicates how energy is expressed in time.',
    aliases: ['MODE', 'QUADRUPLICITY'],
    category: 'astrology',
    relatedTerms: ['cardinal', 'fixed', 'mutable']
  },

  aspect: {
    term: 'ASPECT',
    slug: 'aspect',
    definition: 'An angular relationship between celestial bodies that modulates their interaction. In Cycles in Harmony, aspects describe relational dynamics in the field.',
    aliases: ['ASPECTS'],
    category: 'astrology',
    glyph: '☌ ⚹ □ △ ☍',
    relatedTerms: ['conjunction', 'opposition', 'trine', 'square', 'sextile']
  },
  conjunction: {
    term: 'CONJUNCTION',
    slug: 'conjunction',
    definition: 'An aspect where two bodies occupy the same degree (0°), representing fusion and intensification. In Cycles in Harmony, conjunction is "The Fusion."',
    aliases: [],
    category: 'astrology',
    glyph: '☌',
    relatedTerms: ['aspect', 'orb']
  },
  opposition: {
    term: 'OPPOSITION',
    slug: 'opposition',
    definition: 'An aspect where two bodies are 180° apart, representing polarity and mirroring. In Cycles in Harmony, opposition is "The Mirror."',
    aliases: [],
    category: 'astrology',
    glyph: '☍',
    relatedTerms: ['aspect', 'polarity', 'duality']
  },
  trine: {
    term: 'TRINE',
    slug: 'trine',
    definition: 'An aspect where two bodies are 120° apart, representing flowing support. In Cycles in Harmony, trine is "The Flow."',
    aliases: [],
    category: 'astrology',
    glyph: '△',
    relatedTerms: ['aspect', 'harmony', 'ease']
  },
  square: {
    term: 'SQUARE',
    slug: 'square',
    definition: 'An aspect where two bodies are 90° apart, representing friction and catalyst. In Cycles in Harmony, square is "The Catalyst."',
    aliases: [],
    category: 'astrology',
    glyph: '□',
    relatedTerms: ['aspect', 'tension', 'growth']
  },
  sextile: {
    term: 'SEXTILE',
    slug: 'sextile',
    definition: 'An aspect where two bodies are 60° apart, representing opportunity and opening. In Cycles in Harmony, sextile is "The Opening."',
    aliases: [],
    category: 'astrology',
    glyph: '⚹',
    relatedTerms: ['aspect', 'opportunity']
  },
  orb: {
    term: 'ORB',
    slug: 'orb',
    definition: 'The allowed deviation from exact aspect degree within which an aspect is considered active. Tighter orbs indicate stronger influence.',
    aliases: [],
    category: 'astrology',
    relatedTerms: ['aspect', 'applying', 'separating']
  },
  transit: {
    term: 'TRANSIT',
    slug: 'transit',
    definition: 'The current position of a planet as it moves through the zodiac, activating different points. In Cycles in Harmony, transits describe current field conditions.',
    aliases: ['TRANSITS'],
    category: 'astrology',
    relatedTerms: ['planets', 'progression', 'timing']
  },
  retrograde: {
    term: 'RETROGRADE',
    slug: 'retrograde',
    definition: 'The apparent backward motion of a planet from Earth\'s perspective. In Cycles in Harmony, retrograde periods emphasize review, revision, and internalization.',
    aliases: [],
    category: 'astrology',
    glyph: '℞',
    relatedTerms: ['station', 'direct', 'review']
  },
  station: {
    term: 'STATION',
    slug: 'station',
    definition: 'The moment when a planet appears to stand still before changing direction. In Cycles in Harmony, stations are intensity points.',
    aliases: ['STATIONARY'],
    category: 'astrology',
    relatedTerms: ['retrograde', 'direct', 'threshold']
  },
  direct: {
    term: 'DIRECT',
    slug: 'direct',
    definition: 'The normal forward motion of a planet through the zodiac. In Cycles in Harmony, direct motion supports externalized expression.',
    aliases: [],
    category: 'astrology',
    relatedTerms: ['retrograde', 'station']
  },
  eclipse: {
    term: 'ECLIPSE',
    slug: 'eclipse',
    definition: 'An astronomical event where the Sun, Moon, and Earth align precisely, temporarily blocking or shadowing light. Solar eclipses occur at New Moons near the nodes; lunar eclipses at Full Moons. In Cycles in Harmony, eclipses are threshold moments—portals of amplified significance that can catalyze change or reveal what\'s hidden.',
    aliases: ['ECLIPSES', 'SOLAR ECLIPSE', 'LUNAR ECLIPSE'],
    category: 'astronomy',
    relatedTerms: ['lunar', 'solar', 'node', 'threshold', 'nodalAxis', 'sarosCycle', 'ecliptic'],
    deepDiveLink: { type: 'nodalAxis', key: 'science' }
  },
  node: {
    term: 'NODE',
    slug: 'node',
    definition: 'The two points where the Moon\'s orbital path crosses the ecliptic (Sun\'s apparent path). The North Node (ascending) and South Node (descending) form a developmental axis, moving retrograde through the zodiac every 18.6 years. In Cycles in Harmony, nodes describe evolutionary themes and collective developmental direction.',
    aliases: ['LUNAR NODE', 'LUNAR NODES'],
    category: 'astronomy',
    glyph: '☊ ☋',
    relatedTerms: ['eclipse', 'northNode', 'southNode', 'nodalAxis', 'ecliptic'],
    deepDiveLink: { type: 'nodalAxis', key: 'science' }
  },
  northNode: {
    term: 'NORTH NODE',
    slug: 'northNode',
    definition: 'The ascending lunar node, where the Moon crosses from south to north of the ecliptic. Symbolized by ☊. In Cycles in Harmony and evolutionary astrology, the North Node points toward growth, unfamiliar territory, and qualities to develop.',
    aliases: ['ASCENDING NODE', 'RAHU', 'DRAGON\'S HEAD', 'CAPUT DRACONIS'],
    category: 'astronomy',
    glyph: '☊',
    relatedTerms: ['node', 'southNode', 'nodalAxis', 'eclipse'],
    deepDiveLink: { type: 'nodalAxis', key: 'science' }
  },
  southNode: {
    term: 'SOUTH NODE',
    slug: 'southNode',
    definition: 'The descending lunar node, where the Moon crosses from north to south of the ecliptic. Symbolized by ☋. In Cycles in Harmony and evolutionary astrology, the South Node represents innate gifts, comfort zones, and established patterns to refine rather than abandon.',
    aliases: ['DESCENDING NODE', 'KETU', 'DRAGON\'S TAIL', 'CAUDA DRACONIS'],
    category: 'astronomy',
    glyph: '☋',
    relatedTerms: ['node', 'northNode', 'nodalAxis', 'eclipse'],
    deepDiveLink: { type: 'nodalAxis', key: 'science' }
  },
  nodalAxis: {
    term: 'NODAL AXIS',
    slug: 'nodalAxis',
    definition: 'The polarity formed by the North and South Nodes, always opposite each other in the zodiac. This axis describes a developmental tension between familiar patterns (South) and evolutionary growth (North). The nodal axis moves retrograde, spending ~18 months in each sign pair.',
    aliases: ['NODE AXIS', 'LUNAR NODE AXIS', 'DRAGON AXIS'],
    category: 'astronomy',
    relatedTerms: ['node', 'northNode', 'southNode', 'eclipse', 'ecliptic'],
    deepDiveLink: { type: 'nodalAxis', key: 'science' }
  },
  ecliptic: {
    term: 'ECLIPTIC',
    slug: 'ecliptic',
    definition: 'The apparent path of the Sun across the sky over a year, forming a great circle against the celestial sphere. The zodiac constellations lie along this path. Eclipses can only occur when the Moon is near the ecliptic (at the nodes).',
    aliases: ['ECLIPTIC PLANE'],
    category: 'astronomy',
    relatedTerms: ['zodiac', 'node', 'eclipse', 'celestialEquator']
  },
  sarosCycle: {
    term: 'SAROS CYCLE',
    slug: 'sarosCycle',
    definition: 'An approximately 18-year, 11-day eclipse cycle. Eclipses repeat in similar patterns within a Saros series, shifted ~120° in longitude. Ancient astronomers used Saros cycles to predict eclipses.',
    aliases: ['SAROS', 'SAROS SERIES'],
    category: 'astronomy',
    relatedTerms: ['eclipse', 'node', 'metonicCycle'],
    deepDiveLink: { type: 'nodalAxis', key: 'science' }
  },
  eclipseSeason: {
    term: 'ECLIPSE SEASON',
    slug: 'eclipseSeason',
    definition: 'A ~34-day window occurring every ~173 days when the Sun is close enough to the lunar nodes for eclipses to potentially occur. During eclipse season, a New Moon can produce a solar eclipse and a Full Moon can produce a lunar eclipse.',
    aliases: ['ECLIPSE WINDOW'],
    category: 'astronomy',
    relatedTerms: ['eclipse', 'node', 'lunation'],
    deepDiveLink: { type: 'nodalAxis', key: 'science' }
  },
  stellium: {
    term: 'STELLIUM',
    slug: 'stellium',
    definition: 'Three or more planets concentrated in one sign or house, creating intensified emphasis. In Cycles in Harmony, stelliums describe concentrated field energy.',
    aliases: [],
    category: 'astrology',
    relatedTerms: ['concentration', 'emphasis', 'sign']
  },
  house: {
    term: 'HOUSE',
    slug: 'house',
    definition: 'One of twelve divisions of the chart representing life areas. In Cycles in Harmony, houses describe where cycle energy expresses.',
    aliases: ['HOUSES'],
    category: 'astrology',
    relatedTerms: ['ascendant', 'midheaven', 'sector']
  },
  ascendant: {
    term: 'ASCENDANT',
    slug: 'ascendant',
    definition: 'The zodiac sign rising on the eastern horizon at a given moment, representing the self\'s interface with the world.',
    aliases: ['RISING', 'ASC'],
    category: 'astrology',
    relatedTerms: ['house', 'descendant', 'angle']
  },
  midheaven: {
    term: 'MIDHEAVEN',
    slug: 'midheaven',
    definition: 'The highest point of the chart, representing public life, career, and visible achievement.',
    aliases: ['MC', 'MEDIUM COELI'],
    category: 'astrology',
    relatedTerms: ['house', 'angle', 'career']
  },

  // =========================================================================
  // HELLENISTIC (Classical Astrological Techniques)
  // =========================================================================
  sect: {
    term: 'SECT',
    slug: 'sect',
    definition: 'The division of planets into diurnal (day) and nocturnal (night) teams based on the chart sect. In Cycles in Harmony, sect describes which planetary voices are most aligned with the native\'s fundamental orientation.',
    aliases: ['DIURNAL', 'NOCTURNAL'],
    category: 'hellenistic',
    relatedTerms: ['benefic', 'malefic', 'luminary']
  },
  domicile: {
    term: 'DOMICILE',
    slug: 'domicile',
    definition: 'A planet\'s home sign where it expresses most naturally. In Cycles in Harmony, domicile indicates ease and authenticity of expression.',
    aliases: ['RULERSHIP', 'HOME'],
    category: 'hellenistic',
    relatedTerms: ['dignity', 'detriment', 'exaltation']
  },
  detriment: {
    term: 'DETRIMENT',
    slug: 'detriment',
    definition: 'A planet in the sign opposite its domicile, expressing in an unfamiliar mode. In Cycles in Harmony, detriment describes challenge, not failure.',
    aliases: ['EXILE'],
    category: 'hellenistic',
    relatedTerms: ['domicile', 'dignity', 'fall']
  },
  exaltation: {
    term: 'EXALTATION',
    slug: 'exaltation',
    definition: 'A sign where a planet is honored and elevated, expressing with heightened visibility. In Cycles in Harmony, exaltation describes amplification.',
    aliases: [],
    category: 'hellenistic',
    relatedTerms: ['dignity', 'fall', 'domicile']
  },
  fall: {
    term: 'FALL',
    slug: 'fall',
    definition: 'A planet in the sign opposite its exaltation, expressing in a humble or hidden mode. In Cycles in Harmony, fall describes subtlety, not weakness.',
    aliases: ['DEPRESSION'],
    category: 'hellenistic',
    relatedTerms: ['exaltation', 'dignity', 'detriment']
  },
  dignity: {
    term: 'DIGNITY',
    slug: 'dignity',
    definition: 'The strength or status of a planet based on its sign placement. In Cycles in Harmony, dignity describes ease of expression, not moral worth.',
    aliases: ['ESSENTIAL DIGNITY'],
    category: 'hellenistic',
    relatedTerms: ['domicile', 'exaltation', 'detriment', 'fall']
  },
  benefic: {
    term: 'BENEFIC',
    slug: 'benefic',
    definition: 'A planet (Venus, Jupiter) traditionally associated with ease and benefit. In Cycles in Harmony, benefics describe supportive modulation.',
    aliases: ['BENEFICS'],
    category: 'hellenistic',
    relatedTerms: ['malefic', 'sect', 'jupiter', 'venus']
  },
  malefic: {
    term: 'MALEFIC',
    slug: 'malefic',
    definition: 'A planet (Mars, Saturn) traditionally associated with challenge and difficulty. In Cycles in Harmony, malefics describe demanding modulation.',
    aliases: ['MALEFICS'],
    category: 'hellenistic',
    relatedTerms: ['benefic', 'sect', 'mars', 'saturn'],
    constraints: ['Avoid framing as inherently bad.']
  },
  lot: {
    term: 'LOT',
    slug: 'lot',
    definition: 'A calculated point based on distances between planets and angles. In Hellenistic tradition, lots reveal specific life themes.',
    aliases: ['ARABIC PART', 'LOT OF FORTUNE', 'LOT OF SPIRIT'],
    category: 'hellenistic',
    relatedTerms: ['fortune', 'spirit', 'calculation']
  },
  fortune: {
    term: 'LOT OF FORTUNE',
    slug: 'fortune',
    definition: 'A lot calculated from the Sun, Moon, and Ascendant representing bodily well-being, circumstances, and material fate.',
    aliases: ['PART OF FORTUNE'],
    category: 'hellenistic',
    glyph: '⊗',
    relatedTerms: ['lot', 'spirit', 'moon']
  },
  spirit: {
    term: 'LOT OF SPIRIT',
    slug: 'spirit',
    definition: 'A lot calculated from the Sun, Moon, and Ascendant (reversed from Fortune) representing soul purpose and intentional action.',
    aliases: ['PART OF SPIRIT', 'DAIMON'],
    category: 'hellenistic',
    relatedTerms: ['lot', 'fortune', 'sun']
  },
  bounds: {
    term: 'BOUNDS',
    slug: 'bounds',
    definition: 'Unequal divisions of each sign assigned to different planets, providing fine-grained dignity analysis. Also called terms.',
    aliases: ['TERMS', 'CONFINES'],
    category: 'hellenistic',
    relatedTerms: ['dignity', 'decan', 'sign']
  },
  decan: {
    term: 'DECAN',
    slug: 'decan',
    definition: 'A 10° division of each sign, three per sign, each ruled by a different planet. Decans provide nuance within signs.',
    aliases: ['DECANS', 'FACE'],
    category: 'hellenistic',
    relatedTerms: ['sign', 'bounds', 'dignity']
  },
  profection: {
    term: 'PROFECTION',
    slug: 'profection',
    definition: 'A time-lord technique advancing the Ascendant one sign per year. In Cycles in Harmony, profections describe annual themes.',
    aliases: ['ANNUAL PROFECTION'],
    category: 'hellenistic',
    relatedTerms: ['timelord', 'year', 'house']
  },
  timelord: {
    term: 'TIME LORD',
    slug: 'timelord',
    definition: 'A planet activated as ruler of a particular time period. In Cycles in Harmony, time lords describe which planetary archetype colors a period.',
    aliases: ['CHRONOCRATOR'],
    category: 'hellenistic',
    relatedTerms: ['profection', 'zodiacal releasing', 'timing']
  },
  zodiacalReleasing: {
    term: 'ZODIACAL RELEASING',
    slug: 'zodiacal-releasing',
    definition: 'A Hellenistic technique for timing major life periods based on the Lot of Fortune or Spirit. In Cycles in Harmony, describes life chapters.',
    aliases: ['ZR'],
    category: 'hellenistic',
    relatedTerms: ['lot', 'fortune', 'spirit', 'timelord']
  },
  hayz: {
    term: 'HAYZ',
    slug: 'hayz',
    definition: 'A condition of accidental dignity when a planet is in its preferred sect, above/below horizon appropriately, and in a compatible sign.',
    aliases: ['HALB'],
    category: 'hellenistic',
    relatedTerms: ['sect', 'dignity', 'accidental']
  },

  // =========================================================================
  // SPACE WEATHER & GEOMAGNETIC (Solar-Terrestrial Environment)
  // =========================================================================
  spaceWeather: {
    term: 'SPACE WEATHER',
    slug: 'space-weather',
    definition: 'Conditions in the solar wind, magnetosphere, and ionosphere that affect Earth. In Cycles in Harmony, space weather provides an additional environmental context layer.',
    aliases: ['SOLAR WEATHER', 'GEOSPACE WEATHER'],
    category: 'physics',
    relatedTerms: ['solarCycle', 'geomagneticField', 'kpIndex', 'solarFlare']
  },
  solarCycle: {
    term: 'SOLAR CYCLE',
    slug: 'solar-cycle',
    definition: 'The approximately 11-year cycle of solar activity from minimum to maximum. In Cycles in Harmony, provides macro-level modulation context for field conditions.',
    aliases: ['SUNSPOT CYCLE'],
    category: 'physics',
    relatedTerms: ['spaceWeather', 'solarFlare', 'solarMinimum', 'solarMaximum']
  },
  solarFlare: {
    term: 'SOLAR FLARE',
    slug: 'solar-flare',
    definition: 'An intense burst of radiation from the Sun. In Cycles in Harmony, significant flares may represent threshold events in the field.',
    aliases: ['FLARE'],
    category: 'physics',
    relatedTerms: ['spaceWeather', 'threshold', 'cme']
  },
  cme: {
    term: 'CME',
    slug: 'cme',
    definition: 'Coronal Mass Ejection - a release of plasma and magnetic field from the solar corona. In Cycles in Harmony, CMEs represent significant field modulation events.',
    aliases: ['CORONAL MASS EJECTION'],
    category: 'physics',
    relatedTerms: ['spaceWeather', 'solarFlare', 'geomagneticStorm']
  },
  kpIndex: {
    term: 'KP INDEX',
    slug: 'kp-index',
    definition: 'A measure of geomagnetic disturbance on a 0-9 scale. In Cycles in Harmony, Kp provides quantified context for field intensity.',
    aliases: ['KP', 'GEOMAGNETIC INDEX'],
    category: 'physics',
    relatedTerms: ['geomagneticField', 'spaceWeather', 'aurora']
  },
  geomagneticField: {
    term: 'GEOMAGNETIC FIELD',
    slug: 'geomagnetic-field',
    definition: 'Earth\'s magnetic field that extends into space. In Cycles in Harmony, the geomagnetic field represents a local modulator of cosmic influences.',
    aliases: ['EARTH\'S MAGNETIC FIELD', 'MAGNETOSPHERE'],
    category: 'physics',
    relatedTerms: ['kpIndex', 'spaceWeather', 'aurora']
  },
  geomagneticStorm: {
    term: 'GEOMAGNETIC STORM',
    slug: 'geomagnetic-storm',
    definition: 'A temporary disturbance of Earth\'s magnetosphere caused by solar wind. In Cycles in Harmony, storms represent threshold events requiring adaptation.',
    aliases: ['MAGNETIC STORM', 'G-STORM'],
    category: 'physics',
    relatedTerms: ['kpIndex', 'cme', 'threshold', 'aurora']
  },
  aurora: {
    term: 'AURORA',
    slug: 'aurora',
    definition: 'The light display caused by charged particles entering Earth\'s atmosphere. In Cycles in Harmony, auroras represent visible manifestation of field conditions.',
    aliases: ['NORTHERN LIGHTS', 'AURORA BOREALIS', 'SOUTHERN LIGHTS'],
    category: 'physics',
    relatedTerms: ['geomagneticField', 'kpIndex', 'spaceWeather']
  },
  solarMinimum: {
    term: 'SOLAR MINIMUM',
    slug: 'solar-minimum',
    definition: 'The period of lowest solar activity in the solar cycle. In Cycles in Harmony, solar minimum represents a quieter background field state.',
    aliases: [],
    category: 'physics',
    relatedTerms: ['solarCycle', 'solarMaximum', 'spaceWeather']
  },
  solarMaximum: {
    term: 'SOLAR MAXIMUM',
    slug: 'solar-maximum',
    definition: 'The period of highest solar activity in the solar cycle. In Cycles in Harmony, solar maximum represents heightened field dynamism and potential threshold events.',
    aliases: [],
    category: 'physics',
    relatedTerms: ['solarCycle', 'solarMinimum', 'spaceWeather', 'threshold']
  },

  // =========================================================================
  // PHYSICS (Physical & Quantum Concepts)
  // =========================================================================
  quantum: {
    term: 'QUANTUM',
    slug: 'quantum',
    definition: 'A term referencing phenomena at very small scales in physics. In Cycles in Harmony, quantum language may be used cautiously as metaphor or parallel inquiry without claiming equivalence.',
    aliases: [],
    category: 'physics',
    relatedTerms: ['quantumPhysics', 'nonLocality', 'entanglement'],
    constraints: ['Do not present as scientific proof of CiH.']
  },
  quantumEntanglement: {
    term: 'QUANTUM ENTANGLEMENT',
    slug: 'quantum-entanglement',
    definition: 'A phenomenon in which correlated states persist across separation. In Cycles in Harmony, referenced as a metaphor for distributed relationship without claiming identical mechanism.',
    aliases: ['ENTANGLEMENT'],
    category: 'physics',
    relatedTerms: ['nonLocality', 'relationship'],
    constraints: ['Metaphor/parallel inquiry only.']
  },
  superposition: {
    term: 'SUPERPOSITION',
    slug: 'superposition',
    definition: 'A quantum state where a system exists in multiple states simultaneously until measured. In Cycles in Harmony, a metaphor for potential before expression.',
    aliases: [],
    category: 'physics',
    relatedTerms: ['quantum', 'potential', 'collapse'],
    constraints: ['Metaphor only.']
  },
  observerEffect: {
    term: 'OBSERVER EFFECT',
    slug: 'observer-effect',
    definition: 'The phenomenon where observation influences what is observed. In Cycles in Harmony, acknowledges that attention shapes experience.',
    aliases: [],
    category: 'physics',
    relatedTerms: ['attention', 'quantum', 'measurement'],
    constraints: ['Metaphor only.']
  },
  waveParticle: {
    term: 'WAVE-PARTICLE DUALITY',
    slug: 'wave-particle',
    definition: 'The quantum phenomenon where light and matter exhibit both wave and particle properties. In Cycles in Harmony, a metaphor for complementary truths.',
    aliases: [],
    category: 'physics',
    relatedTerms: ['duality', 'quantum', 'complementarity'],
    constraints: ['Metaphor only.']
  },
  fieldTheory: {
    term: 'FIELD THEORY',
    slug: 'field-theory',
    definition: 'Physics theories describing reality as continuous fields rather than discrete particles. In Cycles in Harmony, supports the "field" metaphor for context.',
    aliases: [],
    category: 'physics',
    relatedTerms: ['field', 'unified', 'continuous']
  },
  nonLocality: {
    term: 'NON-LOCALITY',
    slug: 'non-locality',
    definition: 'The property of quantum systems to exhibit correlations independent of spatial separation. In Cycles in Harmony, a metaphor for interconnection.',
    aliases: [],
    category: 'physics',
    relatedTerms: ['quantumEntanglement', 'connection'],
    constraints: ['Metaphor only.']
  },
  coherence: {
    term: 'COHERENCE',
    slug: 'coherence',
    definition: 'In physics, the maintenance of phase relationships. In Cycles in Harmony, coherence describes integration and alignment of multiple elements.',
    aliases: [],
    category: 'physics',
    relatedTerms: ['harmony', 'alignment', 'integration']
  },
  interference: {
    term: 'INTERFERENCE',
    slug: 'interference',
    definition: 'The combination of waves producing reinforcement or cancellation. In Cycles in Harmony, describes how multiple influences combine.',
    aliases: [],
    category: 'physics',
    relatedTerms: ['resonance', 'modulation', 'combination']
  },
  entropy: {
    term: 'ENTROPY',
    slug: 'entropy',
    definition: 'A measure of disorder or randomness in a system. In Cycles in Harmony, entropy describes the natural tendency toward dispersal, honored in integrate phases.',
    aliases: [],
    category: 'physics',
    relatedTerms: ['order', 'chaos', 'integrate']
  },
  negentropy: {
    term: 'NEGENTROPY',
    slug: 'negentropy',
    definition: 'The tendency of living systems to create order and complexity. In Cycles in Harmony, negentropy describes the creative, organizing aspect of life.',
    aliases: ['SYNTROPY'],
    category: 'physics',
    relatedTerms: ['entropy', 'life', 'organization']
  },
  frequencyPhysics: {
    term: 'FREQUENCY',
    slug: 'frequency-physics',
    definition: 'The rate of oscillation or vibration. In Cycles in Harmony, frequency is a metaphor for the quality or "pitch" of different states.',
    aliases: ['VIBRATION'],
    category: 'physics',
    relatedTerms: ['resonance', 'rhythm', 'oscillation']
  },

  // =========================================================================
  // ASTRONOMY (Observational Celestial Science)
  // =========================================================================
  astronomy: {
    term: 'ASTRONOMY',
    slug: 'astronomy',
    definition: 'The scientific study of celestial objects and phenomena. In Cycles in Harmony, astronomy provides the observational foundation while astrology provides the symbolic layer.',
    aliases: [],
    category: 'astronomy',
    relatedTerms: ['astrology', 'observation', 'science']
  },
  celestial: {
    term: 'CELESTIAL',
    slug: 'celestial',
    definition: 'Relating to the sky and heavenly bodies. In Cycles in Harmony, celestial patterns provide one layer of orientation.',
    aliases: [],
    category: 'astronomy',
    relatedTerms: ['astronomy', 'sky', 'cosmos']
  },
  declination: {
    term: 'DECLINATION',
    slug: 'declination',
    definition: 'A celestial body\'s angular distance north or south of the celestial equator. Declination affects visibility and seasonal timing.',
    aliases: [],
    category: 'astronomy',
    relatedTerms: ['equator', 'latitude', 'position']
  },
  precession: {
    term: 'PRECESSION',
    slug: 'precession',
    definition: 'The slow wobble of Earth\'s axis creating a shift in the position of the equinoxes. This causes the tropical and sidereal zodiacs to diverge.',
    aliases: ['PRECESSION OF THE EQUINOXES'],
    category: 'astronomy',
    relatedTerms: ['tropical', 'sidereal', 'ayanamsa']
  },
  tropical: {
    term: 'TROPICAL ZODIAC',
    slug: 'tropical',
    definition: 'The zodiac defined by the seasons, beginning at the vernal equinox. Cycles in Harmony primarily uses the tropical zodiac.',
    aliases: [],
    category: 'astronomy',
    relatedTerms: ['sidereal', 'equinox', 'zodiac']
  },
  sidereal: {
    term: 'SIDEREAL ZODIAC',
    slug: 'sidereal',
    definition: 'The zodiac defined by the fixed stars, used in Vedic astrology. Cycles in Harmony acknowledges both systems without claiming one is correct.',
    aliases: [],
    category: 'astronomy',
    relatedTerms: ['tropical', 'precession', 'zodiac']
  },
  lunation: {
    term: 'LUNATION',
    slug: 'lunation',
    definition: 'A complete lunar cycle from one New Moon to the next, approximately 29.5 days.',
    aliases: ['LUNAR MONTH', 'SYNODIC MONTH'],
    category: 'astronomy',
    relatedTerms: ['moon', 'newMoon', 'fullMoon']
  },
  newMoon: {
    term: 'NEW MOON',
    slug: 'new-moon',
    definition: 'The lunar phase when Sun and Moon conjoin, appearing dark. In Cycles in Harmony, New Moon marks NOURISH phase beginnings.',
    aliases: [],
    category: 'astronomy',
    glyph: '🌑',
    relatedTerms: ['moon', 'lunation', 'nourish']
  },
  fullMoon: {
    term: 'FULL MOON',
    slug: 'full-moon',
    definition: 'The lunar phase when Sun and Moon oppose, appearing fully illuminated. In Cycles in Harmony, Full Moon marks FULFILL phase.',
    aliases: [],
    category: 'astronomy',
    glyph: '🌕',
    relatedTerms: ['moon', 'lunation', 'fulfill']
  },

  // =========================================================================
  // CYCLE PHASES (Phase-Specific Concepts)
  // =========================================================================
  nourish: {
    term: 'NOURISH',
    slug: 'nourish',
    definition: 'The first phase of the Cycles in Harmony cycle characterized by rest, incubation, listening, repair, and quiet clarity. The field is drawing energy inward.',
    extendedDefinition: 'NOURISH represents the essential foundation of all cycles. Just as winter provides necessary dormancy for spring emergence, and the new moon creates darkness before light returns, this phase creates conditions for all future growth. Energy moves from periphery toward center. External activity decreases while internal processing intensifies.',
    aliases: ['NOURISHMENT'],
    category: 'cyclePhase',
    relatedTerms: ['phase', 'cycle', 'rest', 'potential', 'initiation', 'new_moon', 'winter'],
    correspondences: {
      lunar: 'New Moon to First Quarter',
      season: 'Winter',
      timeOfDay: 'Night (Midnight to Dawn)',
      direction: 'Inward',
      quality: 'Yin',
      element: 'Water/Earth',
      alchemical: 'Nigredo (Blackening)'
    },
    faqs: [
      {
        q: 'How do I know if I\'m in a NOURISH phase?',
        a: 'Signals include: natural desire for solitude, increased need for sleep, heightened sensitivity to environment, vivid dreams and intuitions, and creative incubation beneath the surface.'
      },
      {
        q: 'What activities support NOURISH?',
        a: 'Honor rest without guilt, reduce external commitments, create sanctuary space, listen more than speak, tend to basic needs, practice receptive awareness, and allow projects to incubate.'
      },
      {
        q: 'What are the shadow risks of NOURISH?',
        a: 'Depression masquerading as necessary rest, isolation becoming avoidance, over-withdrawal from responsibilities, using introversion to escape challenges.'
      }
    ]
  },
  activate: {
    term: 'ACTIVATE',
    slug: 'activate',
    definition: 'The second phase of the Cycles in Harmony cycle characterized by initiation, effort, alignment, response, and testing. The field is expanding outward.',
    extendedDefinition: 'ACTIVATE represents the transition from possibility to actuality. Like spring melting winter stillness, this phase initiates movement, growth, and outward expression. This isn\'t merely physical action but the alignment of will with direction.',
    aliases: ['ACTIVATION'],
    category: 'cyclePhase',
    relatedTerms: ['phase', 'cycle', 'expression', 'dynamic', 'first_quarter', 'spring'],
    correspondences: {
      lunar: 'First Quarter to Full Moon',
      season: 'Spring',
      timeOfDay: 'Morning (Dawn to Noon)',
      direction: 'Outward',
      quality: 'Yang',
      element: 'Fire/Air',
      alchemical: 'Albedo (Whitening)'
    },
    faqs: [
      {
        q: 'How do I know if I\'m in an ACTIVATE phase?',
        a: 'Signals include: natural energy for action, clarity about direction, enthusiasm emerging, frustration with stagnation, physical vitality increasing, ideas wanting to become concrete.'
      },
      {
        q: 'What activities support ACTIVATE?',
        a: 'Begin - even imperfectly, convert inspiration to action quickly, build momentum through consistent effort, align actions with intentions, communicate actively, exercise and move the body.'
      },
      {
        q: 'What are the shadow risks of ACTIVATE?',
        a: 'Starting too many things simultaneously, action without reflection, burnout from unsustainable pace, aggression where patience serves better.'
      }
    ]
  },
  fulfill: {
    term: 'FULFILL',
    slug: 'fulfill',
    definition: 'The third phase of the Cycles in Harmony cycle characterized by illumination, celebration, recognition, fullness, and harvest. The field is at maximum visibility.',
    extendedDefinition: 'FULFILL represents the culmination of growth and peak visibility. Like summer\'s abundance or the full moon\'s complete illumination, this phase marks when what was building reaches fullest expression. This isn\'t only about achievement but about recognition.',
    aliases: ['FULFILLMENT', 'FULL'],
    category: 'cyclePhase',
    relatedTerms: ['phase', 'cycle', 'realization', 'expression', 'full_moon', 'summer'],
    correspondences: {
      lunar: 'Full Moon',
      season: 'Summer',
      timeOfDay: 'Afternoon (Noon to Dusk)',
      direction: 'Peak Outward',
      quality: 'Yang (Maximum)',
      element: 'Fire/Air',
      alchemical: 'Citrinitas (Yellowing)'
    },
    faqs: [
      {
        q: 'How do I know if I\'m in a FULFILL phase?',
        a: 'Signals include: sense of completion or arrival, desire to celebrate and share, clarity about accomplishments, recognition from self or others, peak energy and visibility.'
      },
      {
        q: 'What activities support FULFILL?',
        a: 'Celebrate genuinely without deflection, receive recognition gracefully, share abundance with others, document accomplishments, be fully present to the peak moment.'
      },
      {
        q: 'What are the shadow risks of FULFILL?',
        a: 'Inability to receive or celebrate, forcing peak states to continue indefinitely, deflecting recognition through false modesty, anxiety about inevitable decline.'
      }
    ]
  },
  integrate: {
    term: 'INTEGRATE',
    slug: 'integrate',
    definition: 'The fourth phase of the Cycles in Harmony cycle characterized by discernment, simplification, wisdom, letting go, and closure. The field is turning back inward.',
    extendedDefinition: 'INTEGRATE represents the essential work of editing, releasing, and distilling. Like autumn\'s falling leaves returning nutrients to soil, this phase transforms experience into wisdom. Not everything that emerged deserves continuation.',
    aliases: ['INTEGRATION'],
    category: 'cyclePhase',
    relatedTerms: ['phase', 'cycle', 'release', 'reflection', 'waning_moon', 'autumn'],
    correspondences: {
      lunar: 'Waning Moon to New Moon',
      season: 'Autumn',
      timeOfDay: 'Evening (Dusk to Midnight)',
      direction: 'Inward (Returning)',
      quality: 'Yin (Returning)',
      element: 'Water/Earth',
      alchemical: 'Rubedo (Reddening)'
    },
    faqs: [
      {
        q: 'How do I know if I\'m in an INTEGRATE phase?',
        a: 'Signals include: natural desire to simplify and reduce, clarity about what matters, completion impulses, reflective mood, some appropriate melancholy as things end.'
      },
      {
        q: 'What activities support INTEGRATE?',
        a: 'Practice discernment actively, release what no longer serves, complete unfinished cycles, simplify commitments, extract wisdom from experience, honor endings with ritual.'
      },
      {
        q: 'What are the shadow risks of INTEGRATE?',
        a: 'Premature release (throwing out what still has value), inability to let go, depression masquerading as discernment, rushing to next cycle without completing this one.'
      }
    ]
  },
  potential: {
    term: 'POTENTIAL',
    slug: 'potential',
    definition: 'The capacity for emergence within a cycle before form is visible. In Cycles in Harmony, potential is real without being guaranteed.',
    aliases: ['LATENT POTENTIAL', 'SEED POTENTIAL'],
    category: 'cyclePhase',
    relatedTerms: ['nourish', 'initiation', 'readiness', 'capacity']
  },
  realization: {
    term: 'REALIZATION',
    slug: 'realization',
    definition: 'The moment when an insight, pattern, or condition becomes clear enough to be recognized and integrated. In Cycles in Harmony, realization can be sudden or gradual.',
    aliases: ['REALIZE'],
    category: 'cyclePhase',
    relatedTerms: ['fulfill', 'recognition', 'integrate', 'clarity']
  },

  // =========================================================================
  // PROCESS (Transformation & Change)
  // =========================================================================
  alchemy: {
    term: 'ALCHEMY',
    slug: 'alchemy',
    definition: 'The ancient art of transformation, symbolically describing processes of purification and transmutation. In Cycles in Harmony, alchemical language offers rich metaphors for change.',
    aliases: [],
    category: 'process',
    relatedTerms: ['transformation', 'process', 'symbols'],
    constraints: ['Metaphor only.']
  },
  nigredo: {
    term: 'NIGREDO',
    slug: 'nigredo',
    definition: 'The blackening stage of alchemy representing dissolution and darkness before transformation. In Cycles in Harmony, corresponds to NOURISH-like phases.',
    aliases: ['BLACKENING'],
    category: 'process',
    relatedTerms: ['alchemy', 'nourish', 'dissolution']
  },
  albedo: {
    term: 'ALBEDO',
    slug: 'albedo',
    definition: 'The whitening stage of alchemy representing purification and washing. In Cycles in Harmony, corresponds to ACTIVATE-like clearing.',
    aliases: ['WHITENING'],
    category: 'process',
    relatedTerms: ['alchemy', 'activate', 'purification']
  },
  citrinitas: {
    term: 'CITRINITAS',
    slug: 'citrinitas',
    definition: 'The yellowing stage of alchemy representing solar illumination. In Cycles in Harmony, corresponds to FULFILL-like revelation.',
    aliases: ['YELLOWING'],
    category: 'process',
    relatedTerms: ['alchemy', 'fulfill', 'illumination']
  },
  rubedo: {
    term: 'RUBEDO',
    slug: 'rubedo',
    definition: 'The reddening stage of alchemy representing final integration and completion. In Cycles in Harmony, corresponds to INTEGRATE-like wholeness.',
    aliases: ['REDDENING'],
    category: 'process',
    relatedTerms: ['alchemy', 'integrate', 'completion']
  },

  // =========================================================================
  // SYMBOLS (Glyphs & Notation)
  // =========================================================================
  glyph: {
    term: 'GLYPH',
    slug: 'glyph',
    definition: 'A symbolic character representing a celestial body, sign, or aspect. In Cycles in Harmony, glyphs provide visual shorthand.',
    aliases: ['SYMBOL', 'SIGIL'],
    category: 'symbols',
    relatedTerms: ['symbol', 'notation']
  },
  aries: {
    term: 'ARIES',
    slug: 'aries',
    definition: 'The first sign of the zodiac, cardinal fire. Aries initiates the solar year and emphasizes action, courage, and beginning.',
    aliases: [],
    category: 'astrology',
    glyph: '♈',
    relatedTerms: ['fire', 'cardinal', 'mars']
  },
  taurus: {
    term: 'TAURUS',
    slug: 'taurus',
    definition: 'The second sign, fixed earth. Taurus emphasizes stability, sensuality, and values.',
    aliases: [],
    category: 'astrology',
    glyph: '♉',
    relatedTerms: ['earth', 'fixed', 'venus']
  },
  gemini: {
    term: 'GEMINI',
    slug: 'gemini',
    definition: 'The third sign, mutable air. Gemini emphasizes communication, curiosity, and duality.',
    aliases: [],
    category: 'astrology',
    glyph: '♊',
    relatedTerms: ['air', 'mutable', 'mercury']
  },
  cancer: {
    term: 'CANCER',
    slug: 'cancer',
    definition: 'The fourth sign, cardinal water. Cancer emphasizes nurturing, home, and emotional security.',
    aliases: [],
    category: 'astrology',
    glyph: '♋',
    relatedTerms: ['water', 'cardinal', 'moon']
  },
  leo: {
    term: 'LEO',
    slug: 'leo',
    definition: 'The fifth sign, fixed fire. Leo emphasizes creativity, self-expression, and radiance.',
    aliases: [],
    category: 'astrology',
    glyph: '♌',
    relatedTerms: ['fire', 'fixed', 'sun']
  },
  virgo: {
    term: 'VIRGO',
    slug: 'virgo',
    definition: 'The sixth sign, mutable earth. Virgo emphasizes analysis, service, and refinement.',
    aliases: [],
    category: 'astrology',
    glyph: '♍',
    relatedTerms: ['earth', 'mutable', 'mercury']
  },
  libra: {
    term: 'LIBRA',
    slug: 'libra',
    definition: 'The seventh sign, cardinal air. Libra emphasizes balance, relationship, and aesthetics.',
    aliases: [],
    category: 'astrology',
    glyph: '♎',
    relatedTerms: ['air', 'cardinal', 'venus']
  },
  scorpio: {
    term: 'SCORPIO',
    slug: 'scorpio',
    definition: 'The eighth sign, fixed water. Scorpio emphasizes depth, transformation, and intensity.',
    aliases: [],
    category: 'astrology',
    glyph: '♏',
    relatedTerms: ['water', 'fixed', 'mars', 'pluto']
  },
  sagittarius: {
    term: 'SAGITTARIUS',
    slug: 'sagittarius',
    definition: 'The ninth sign, mutable fire. Sagittarius emphasizes expansion, philosophy, and adventure.',
    aliases: [],
    category: 'astrology',
    glyph: '♐',
    relatedTerms: ['fire', 'mutable', 'jupiter']
  },
  capricorn: {
    term: 'CAPRICORN',
    slug: 'capricorn',
    definition: 'The tenth sign, cardinal earth. Capricorn emphasizes structure, achievement, and mastery.',
    aliases: [],
    category: 'astrology',
    glyph: '♑',
    relatedTerms: ['earth', 'cardinal', 'saturn']
  },
  aquarius: {
    term: 'AQUARIUS',
    slug: 'aquarius',
    definition: 'The eleventh sign, fixed air. Aquarius emphasizes innovation, community, and ideals.',
    aliases: [],
    category: 'astrology',
    glyph: '♒',
    relatedTerms: ['air', 'fixed', 'saturn', 'uranus']
  },
  pisces: {
    term: 'PISCES',
    slug: 'pisces',
    definition: 'The twelfth sign, mutable water. Pisces emphasizes dissolution, spirituality, and transcendence.',
    aliases: [],
    category: 'astrology',
    glyph: '♓',
    relatedTerms: ['water', 'mutable', 'jupiter', 'neptune']
  },
  mercury: {
    term: 'MERCURY',
    slug: 'mercury',
    definition: 'The planet governing communication, thought, and exchange. In Cycles in Harmony, Mercury modulates mental and communicative emphasis.',
    aliases: [],
    category: 'astrology',
    glyph: '☿',
    relatedTerms: ['planets', 'gemini', 'virgo', 'retrograde']
  },
  venus: {
    term: 'VENUS',
    slug: 'venus',
    definition: 'The planet governing love, beauty, and values. In Cycles in Harmony, Venus modulates relational and aesthetic emphasis.',
    aliases: [],
    category: 'astrology',
    glyph: '♀',
    relatedTerms: ['planets', 'taurus', 'libra', 'benefic']
  },
  mars: {
    term: 'MARS',
    slug: 'mars',
    definition: 'The planet governing action, drive, and assertion. In Cycles in Harmony, Mars modulates active and assertive emphasis.',
    aliases: [],
    category: 'astrology',
    glyph: '♂',
    relatedTerms: ['planets', 'aries', 'scorpio', 'malefic']
  },
  jupiter: {
    term: 'JUPITER',
    slug: 'jupiter',
    definition: 'The planet governing expansion, wisdom, and abundance. In Cycles in Harmony, Jupiter modulates growth and philosophical emphasis.',
    aliases: [],
    category: 'astrology',
    glyph: '♃',
    relatedTerms: ['planets', 'sagittarius', 'pisces', 'benefic']
  },
  saturn: {
    term: 'SATURN',
    slug: 'saturn',
    definition: 'The planet governing structure, limitation, and time. In Cycles in Harmony, Saturn modulates discipline and boundary emphasis.',
    aliases: [],
    category: 'astrology',
    glyph: '♄',
    relatedTerms: ['planets', 'capricorn', 'aquarius', 'malefic']
  },
  uranus: {
    term: 'URANUS',
    slug: 'uranus',
    definition: 'The planet governing innovation, disruption, and awakening. In Cycles in Harmony, Uranus modulates revolutionary and liberating emphasis.',
    aliases: [],
    category: 'astrology',
    glyph: '♅',
    relatedTerms: ['planets', 'aquarius', 'outer']
  },
  neptune: {
    term: 'NEPTUNE',
    slug: 'neptune',
    definition: 'The planet governing dissolution, dreams, and transcendence. In Cycles in Harmony, Neptune modulates spiritual and imaginative emphasis.',
    aliases: [],
    category: 'astrology',
    glyph: '♆',
    relatedTerms: ['planets', 'pisces', 'outer']
  },
  pluto: {
    term: 'PLUTO',
    slug: 'pluto',
    definition: 'The dwarf planet governing transformation, power, and depth. In Cycles in Harmony, Pluto modulates transformative and regenerative emphasis.',
    aliases: [],
    category: 'astrology',
    glyph: '♇',
    relatedTerms: ['planets', 'scorpio', 'outer']
  },
  chiron: {
    term: 'CHIRON',
    slug: 'chiron',
    definition: 'A minor planet/comet governing wounding, healing, and mentorship. In Cycles in Harmony, Chiron modulates healing and teaching emphasis.',
    aliases: ['WOUNDED HEALER'],
    category: 'astrology',
    glyph: '⚷',
    relatedTerms: ['healing', 'teaching', 'wound']
  },

  // =========================================================================
  // BODY SYSTEMS (Anatomical Correspondences)
  // =========================================================================
  bodyCorrespondence: {
    term: 'BODY CORRESPONDENCE',
    slug: 'body-correspondence',
    definition: 'The traditional association between celestial bodies/signs and parts of the physical body. In Cycles in Harmony, correspondences are invitations for somatic awareness, not medical claims.',
    aliases: ['SOMATIC CORRESPONDENCE', 'ANATOMICAL RULERSHIP'],
    category: 'bodySystem',
    relatedTerms: ['embodiment', 'melothesia', 'zodiacal_body'],
    constraints: ['Never medical advice.', 'Awareness tool only.'],
    faqs: [
      { q: 'Are body correspondences medically valid?', a: 'No. They are symbolic associations from ancient traditions. Use them for awareness and reflection, never for diagnosis or treatment.' },
      { q: 'How should I use body correspondences?', a: 'As prompts for somatic check-ins. When Mars is active, notice your head, muscles, energy levels. When Moon is in Cancer, notice chest, digestion, emotional embodiment.' }
    ]
  },
  melothesia: {
    term: 'MELOTHESIA',
    slug: 'melothesia',
    definition: 'The ancient practice of mapping zodiac signs to body parts. The cosmic human (homo signorum) places Aries at the head and Pisces at the feet.',
    aliases: ['ZODIACAL BODY', 'ZODIAC MAN'],
    category: 'bodySystem',
    relatedTerms: ['body-correspondence', 'zodiac', 'embodiment'],
    constraints: ['Historical/symbolic only.']
  },
  headCorrespondence: {
    term: 'HEAD (Aries)',
    slug: 'head-correspondence',
    definition: 'The head, face, brain, and eyes correspond to Aries and Mars. These areas relate to identity, initiative, and how we face the world.',
    aliases: ['CRANIAL CORRESPONDENCE'],
    category: 'bodySystem',
    relatedTerms: ['aries', 'mars', 'body-correspondence'],
    somaticPrompt: 'Notice tension in your jaw, forehead, or around your eyes. How does your head feel when you\'re initiating versus holding back?'
  },
  heartCorrespondence: {
    term: 'HEART (Leo/Sun)',
    slug: 'heart-correspondence',
    definition: 'The heart, spine, and circulatory system correspond to Leo and the Sun. These areas relate to vitality, creative expression, and authentic self.',
    aliases: ['CARDIAC CORRESPONDENCE'],
    category: 'bodySystem',
    relatedTerms: ['leo', 'sun', 'body-correspondence'],
    somaticPrompt: 'Notice your heartbeat, posture, and sense of warmth. How open does your chest feel? Where does your creative energy flow?'
  },
  stomachCorrespondence: {
    term: 'STOMACH (Cancer/Moon)',
    slug: 'stomach-correspondence',
    definition: 'The stomach, breasts, and lymphatic system correspond to Cancer and the Moon. These areas relate to nourishment, emotional processing, and nurturing.',
    aliases: ['DIGESTIVE CORRESPONDENCE'],
    category: 'bodySystem',
    relatedTerms: ['cancer', 'moon', 'body-correspondence'],
    somaticPrompt: 'Notice your gut feelings, hunger signals, and emotional sensations in your belly. How are you nourishing yourself?'
  },
  nervousSystemCorrespondence: {
    term: 'NERVOUS SYSTEM (Mercury/Gemini/Virgo)',
    slug: 'nervous-system-correspondence',
    definition: 'The nervous system, lungs, hands, and arms correspond to Mercury and its signs. These areas relate to communication, connection, and information processing.',
    aliases: ['MERCURIAL CORRESPONDENCE'],
    category: 'bodySystem',
    relatedTerms: ['mercury', 'gemini', 'virgo', 'body-correspondence'],
    somaticPrompt: 'Notice your breath patterns, hand gestures, and mental busyness. Are you overstimulated or understimulated?'
  },
  reproductiveCorrespondence: {
    term: 'REPRODUCTIVE SYSTEM (Scorpio/Pluto)',
    slug: 'reproductive-correspondence',
    definition: 'The reproductive organs, elimination systems, and regenerative processes correspond to Scorpio and Pluto. These relate to transformation, intimacy, and letting go.',
    aliases: ['PLUTONIAN CORRESPONDENCE'],
    category: 'bodySystem',
    relatedTerms: ['scorpio', 'pluto', 'body-correspondence'],
    somaticPrompt: 'Notice your relationship with elimination, release, and regeneration. What are you holding that needs to transform?'
  },
  skeletalCorrespondence: {
    term: 'SKELETAL SYSTEM (Capricorn/Saturn)',
    slug: 'skeletal-correspondence',
    definition: 'The bones, teeth, skin, and knees correspond to Capricorn and Saturn. These relate to structure, boundaries, and how we support ourselves.',
    aliases: ['SATURNIAN CORRESPONDENCE'],
    category: 'bodySystem',
    relatedTerms: ['capricorn', 'saturn', 'body-correspondence'],
    somaticPrompt: 'Notice your posture, joint health, and sense of structural support. Where do you feel solid? Where do you feel brittle?'
  },

  // =========================================================================
  // ARCHETYPES (Universal Patterns)
  // =========================================================================
  archetype: {
    term: 'ARCHETYPE',
    slug: 'archetype',
    definition: 'A universal pattern of meaning that appears across cultures and times. In Cycles in Harmony, archetypes are lenses for understanding, not fixed identities or destinies.',
    aliases: ['ARCHETYPAL PATTERN', 'UNIVERSAL PATTERN'],
    category: 'archetype',
    relatedTerms: ['mythology', 'symbol', 'meaning'],
    faqs: [
      { q: 'Are archetypes real?', a: 'They are real as patterns of meaning, not as entities. They describe recurring themes in human experience across cultures.' },
      { q: 'How do I work with archetypes?', a: 'As lenses for self-understanding. Notice which archetypal patterns resonate with your experience, without reducing yourself to any single one.' }
    ]
  },
  warrior: {
    term: 'WARRIOR',
    slug: 'warrior',
    definition: 'The archetype of action, courage, and assertion. Associated with Mars and Aries. Represents the capacity to assert boundaries, take initiative, and fight for what matters.',
    aliases: ['HERO', 'CHAMPION'],
    category: 'archetype',
    relatedTerms: ['mars', 'aries', 'activate'],
    mythicFigures: 'Ares (Greek), Mars (Roman), Sekhmet (Egyptian), Thor (Norse)',
    shadowExpression: 'Aggression, violence, recklessness, domination'
  },
  nurturer: {
    term: 'NURTURER',
    slug: 'nurturer',
    definition: 'The archetype of care, protection, and emotional support. Associated with the Moon and Cancer. Represents the capacity to hold, feed, and create safety.',
    aliases: ['MOTHER', 'CAREGIVER'],
    category: 'archetype',
    relatedTerms: ['moon', 'cancer', 'nourish'],
    mythicFigures: 'Demeter (Greek), Isis (Egyptian), Frigg (Norse), Guan Yin (Buddhist)',
    shadowExpression: 'Smothering, codependency, martyrdom, emotional manipulation'
  },
  messenger: {
    term: 'MESSENGER',
    slug: 'messenger',
    definition: 'The archetype of communication, connection, and exchange. Associated with Mercury and Gemini. Represents the capacity to translate, transmit, and bridge worlds.',
    aliases: ['TRICKSTER', 'GUIDE'],
    category: 'archetype',
    relatedTerms: ['mercury', 'gemini', 'virgo'],
    mythicFigures: 'Hermes (Greek), Thoth (Egyptian), Loki (Norse), Coyote (Native American)',
    shadowExpression: 'Deception, superficiality, manipulation, scattered attention'
  },
  lover: {
    term: 'LOVER',
    slug: 'lover',
    definition: 'The archetype of beauty, pleasure, and relational connection. Associated with Venus, Taurus, and Libra. Represents the capacity for appreciation, attraction, and harmony.',
    aliases: ['APHRODITE', 'BEAUTY'],
    category: 'archetype',
    relatedTerms: ['venus', 'taurus', 'libra'],
    mythicFigures: 'Aphrodite (Greek), Venus (Roman), Freya (Norse), Hathor (Egyptian)',
    shadowExpression: 'Vanity, possessiveness, superficiality, dependency'
  },
  sovereign: {
    term: 'SOVEREIGN',
    slug: 'sovereign',
    definition: 'The archetype of creative authority, radiance, and leadership. Associated with the Sun and Leo. Represents the capacity for authentic self-expression and generous leadership.',
    aliases: ['KING', 'QUEEN', 'RULER'],
    category: 'archetype',
    relatedTerms: ['sun', 'leo', 'fulfill'],
    mythicFigures: 'Apollo (Greek), Ra (Egyptian), Zeus (Greek), Amaterasu (Japanese)',
    shadowExpression: 'Tyranny, narcissism, need for constant validation, dramatic attention-seeking'
  },
  sage: {
    term: 'SAGE',
    slug: 'sage',
    definition: 'The archetype of wisdom, knowledge, and philosophical understanding. Associated with Jupiter and Sagittarius. Represents the capacity for meaning-making and teaching.',
    aliases: ['PHILOSOPHER', 'TEACHER', 'GURU'],
    category: 'archetype',
    relatedTerms: ['jupiter', 'sagittarius', 'integrate'],
    mythicFigures: 'Chiron (Greek), Odin (Norse), Thoth (Egyptian), Athena (Greek)',
    shadowExpression: 'Know-it-all, dogmatism, preachiness, spiritual bypassing'
  },
  elder: {
    term: 'ELDER',
    slug: 'elder',
    definition: 'The archetype of time, discipline, and earned authority. Associated with Saturn and Capricorn. Represents the capacity for patience, structure, and wisdom through experience.',
    aliases: ['FATHER', 'AUTHORITY', 'BUILDER'],
    category: 'archetype',
    relatedTerms: ['saturn', 'capricorn', 'structure'],
    mythicFigures: 'Kronos (Greek), Saturn (Roman), Ptah (Egyptian), Father Time',
    shadowExpression: 'Rigidity, pessimism, authoritarianism, cold detachment'
  },
  mystic: {
    term: 'MYSTIC',
    slug: 'mystic',
    definition: 'The archetype of transcendence, dissolution, and spiritual connection. Associated with Neptune and Pisces. Represents the capacity for compassion, imagination, and unity consciousness.',
    aliases: ['DREAMER', 'VISIONARY', 'POET'],
    category: 'archetype',
    relatedTerms: ['neptune', 'pisces', 'nourish'],
    mythicFigures: 'Dionysus (Greek), Poseidon (Greek), Vishnu (Hindu), Mary (Christian)',
    shadowExpression: 'Escapism, delusion, addiction, martyrdom, boundary dissolution'
  },
  transformer: {
    term: 'TRANSFORMER',
    slug: 'transformer',
    definition: 'The archetype of death, rebirth, and profound change. Associated with Pluto and Scorpio. Represents the capacity for facing shadow, releasing what must die, and regenerating.',
    aliases: ['PHOENIX', 'SHAMAN', 'DEATH'],
    category: 'archetype',
    relatedTerms: ['pluto', 'scorpio', 'integrate'],
    mythicFigures: 'Hades (Greek), Osiris (Egyptian), Kali (Hindu), Persephone (Greek)',
    shadowExpression: 'Destructiveness, obsession, control, inability to let go'
  },

  // =========================================================================
  // ELEMENTS & MODALITIES (Qualitative Categories)
  // =========================================================================
  elementalQuadrant: {
    term: 'ELEMENTAL QUADRANT',
    slug: 'elemental-quadrant',
    definition: 'The four-fold division of qualities: Fire (spirit), Earth (matter), Air (mind), Water (emotion). In Cycles in Harmony, elements describe modes of expression and emphasis.',
    aliases: ['TRIPLICITIES', 'ELEMENTAL TRIPLICITY'],
    category: 'elementModality',
    relatedTerms: ['fire', 'earth', 'air', 'water'],
    faqs: [
      { q: 'What do elements actually describe?', a: 'They describe how energy expresses: Fire through action and inspiration, Earth through manifestation and stability, Air through thought and connection, Water through feeling and flow.' },
      { q: 'Can I have multiple elements strong?', a: 'Absolutely. Most people have emphasis in multiple elements. The question is which is dominant in any given moment or area of life.' }
    ]
  },
  fireElement: {
    term: 'FIRE ELEMENT',
    slug: 'fire-element',
    definition: 'The element of spirit, action, and inspiration. Fire signs (Aries, Leo, Sagittarius) emphasize initiative, enthusiasm, and creative spark. Fire corresponds to ACTIVATE energy.',
    aliases: ['FIRE TRIPLICITY'],
    category: 'elementModality',
    relatedTerms: ['aries', 'leo', 'sagittarius', 'activate'],
    characteristics: 'Warm, dry, active, rising, yang',
    bodyAssociation: 'Temperature, metabolism, inflammation, vital force',
    psychologicalFunction: 'Will, inspiration, enthusiasm, courage, anger'
  },
  earthElement: {
    term: 'EARTH ELEMENT',
    slug: 'earth-element',
    definition: 'The element of matter, stability, and manifestation. Earth signs (Taurus, Virgo, Capricorn) emphasize practicality, sensory experience, and building. Earth corresponds to NOURISH energy.',
    aliases: ['EARTH TRIPLICITY'],
    category: 'elementModality',
    relatedTerms: ['taurus', 'virgo', 'capricorn', 'nourish'],
    characteristics: 'Cold, dry, stable, grounding, yin',
    bodyAssociation: 'Structure, bones, skin, physical sensation',
    psychologicalFunction: 'Practicality, sensory awareness, patience, stubbornness'
  },
  airElement: {
    term: 'AIR ELEMENT',
    slug: 'air-element',
    definition: 'The element of mind, connection, and exchange. Air signs (Gemini, Libra, Aquarius) emphasize thought, communication, and social awareness. Air corresponds to ACTIVATE/FULFILL energy.',
    aliases: ['AIR TRIPLICITY'],
    category: 'elementModality',
    relatedTerms: ['gemini', 'libra', 'aquarius', 'activate'],
    characteristics: 'Warm, moist, mobile, connecting, yang',
    bodyAssociation: 'Breath, nervous system, circulation, mental processes',
    psychologicalFunction: 'Thinking, relating, communicating, detachment'
  },
  waterElement: {
    term: 'WATER ELEMENT',
    slug: 'water-element',
    definition: 'The element of emotion, depth, and flow. Water signs (Cancer, Scorpio, Pisces) emphasize feeling, intuition, and emotional processing. Water corresponds to NOURISH/INTEGRATE energy.',
    aliases: ['WATER TRIPLICITY'],
    category: ['elementModality'],
    relatedTerms: ['cancer', 'scorpio', 'pisces', 'nourish', 'integrate'],
    characteristics: 'Cold, moist, flowing, receptive, yin',
    bodyAssociation: 'Fluids, emotions, lymphatic system, reproductive system',
    psychologicalFunction: 'Feeling, intuiting, empathizing, absorbing'
  },
  modalQuadrant: {
    term: 'MODAL QUADRANT',
    slug: 'modal-quadrant',
    definition: 'The three-fold division of modes: Cardinal (initiating), Fixed (sustaining), Mutable (adapting). In Cycles in Harmony, modes describe how signs engage with change.',
    aliases: ['QUADRUPLICITIES', 'MODAL TRIPLICITY'],
    category: 'elementModality',
    relatedTerms: ['cardinal', 'fixed', 'mutable'],
    faqs: [
      { q: 'What\'s the difference between elements and modes?', a: 'Elements describe WHAT kind of energy (fire, earth, air, water). Modes describe HOW that energy engages with change (initiating, sustaining, adapting).' },
      { q: 'Why are there four cardinal signs?', a: 'Each mode contains one sign of each element: Cardinal Fire (Aries), Cardinal Earth (Capricorn), Cardinal Air (Libra), Cardinal Water (Cancer).' }
    ]
  },
  cardinalMode: {
    term: 'CARDINAL MODE',
    slug: 'cardinal-mode',
    definition: 'The mode of initiation, leadership, and new beginnings. Cardinal signs (Aries, Cancer, Libra, Capricorn) mark the start of seasons and emphasize starting things.',
    aliases: ['CARDINAL QUADRUPLICITY'],
    category: 'elementModality',
    relatedTerms: ['aries', 'cancer', 'libra', 'capricorn', 'activate'],
    characteristics: 'Initiating, enterprising, leadership-oriented, impatient',
    seasonalCorrespondence: 'Beginning of seasons (equinoxes/solstices)'
  },
  fixedMode: {
    term: 'FIXED MODE',
    slug: 'fixed-mode',
    definition: 'The mode of stability, persistence, and sustaining. Fixed signs (Taurus, Leo, Scorpio, Aquarius) mark the middle of seasons and emphasize maintaining what exists.',
    aliases: ['FIXED QUADRUPLICITY'],
    category: 'elementModality',
    relatedTerms: ['taurus', 'leo', 'scorpio', 'aquarius', 'fulfill'],
    characteristics: 'Sustaining, persistent, determined, stubborn',
    seasonalCorrespondence: 'Middle of seasons (peak intensity)'
  },
  mutableMode: {
    term: 'MUTABLE MODE',
    slug: 'mutable-mode',
    definition: 'The mode of adaptation, transition, and completion. Mutable signs (Gemini, Virgo, Sagittarius, Pisces) mark the end of seasons and emphasize flexibility and change.',
    aliases: ['MUTABLE QUADRUPLICITY'],
    category: 'elementModality',
    relatedTerms: ['gemini', 'virgo', 'sagittarius', 'pisces', 'integrate'],
    characteristics: 'Adapting, flexible, transitional, scattered',
    seasonalCorrespondence: 'End of seasons (transitional)'
  },

  // =========================================================================
  // SABBAT TRADITIONS (Wheel of the Year)
  // =========================================================================
  wheelOfYearTradition: {
    term: 'WHEEL OF THE YEAR',
    slug: 'wheel-of-year-tradition',
    definition: 'The eight-fold seasonal calendar marking solstices, equinoxes, and cross-quarter days. In Cycles in Harmony, the Wheel provides cultural interface points for seasonal attunement.',
    aliases: ['SABBAT WHEEL', 'PAGAN CALENDAR'],
    category: 'sabbatTradition',
    relatedTerms: ['sabbat', 'season', 'threshold'],
    constraints: ['Cultural practice optional.', 'Not required for Cycles in Harmony engagement.'],
    faqs: [
      { q: 'Do I have to celebrate sabbats?', a: 'Absolutely not. Sabbats are optional cultural interfaces. You can engage with seasonal awareness entirely through astronomical observation.' },
      { q: 'Are sabbats religious?', a: 'They have roots in various traditions but can be engaged secularly. In Cycles in Harmony, they\'re treated as awareness prompts, not religious observances.' }
    ]
  },
  yuleTradition: {
    term: 'YULE (Winter Solstice)',
    slug: 'yule-tradition',
    definition: 'The sabbat of Winter Solstice (Dec 21), marking the year\'s longest night and the return of light. Deep NOURISH threshold—birth from darkness.',
    aliases: ['WINTER SOLSTICE', 'MIDWINTER'],
    category: 'sabbatTradition',
    relatedTerms: ['nourish', 'winter', 'solstice', 'capricorn'],
    traditions: 'Yule log lighting, evergreen decorations, candle rituals, gift-giving, feasting',
    ritualFocus: 'Honoring darkness, welcoming returning light, gratitude for shelter, setting intentions',
    natureEvents: 'Longest night, lowest sun angle, dormant vegetation, animal hibernation',
    somaticInvitation: 'Rest deeply. What seeds are germinating in your darkness?'
  },
  imbolcTradition: {
    term: 'IMBOLC',
    slug: 'imbolc-tradition',
    definition: 'The sabbat halfway between Winter Solstice and Spring Equinox (Feb 1-2). First stirrings of ACTIVATE—quickening beneath the surface.',
    aliases: ['CANDLEMAS', 'BRIGID\'S DAY'],
    category: 'sabbatTradition',
    relatedTerms: ['nourish', 'activate', 'aquarius', 'threshold'],
    traditions: 'Candle lighting, spring cleaning, Brigid\'s crosses, milk/dairy offerings, poetry',
    ritualFocus: 'Purification, first intentions, clearing winter stagnation, creative inspiration',
    natureEvents: 'First snowdrops, longer days noticeable, lambing season begins, sap rising',
    somaticInvitation: 'What is stirring in you? What wants to be cleaned out or cleared?'
  },
  ostaraTradition: {
    term: 'OSTARA (Spring Equinox)',
    slug: 'ostara-tradition',
    definition: 'The sabbat of Spring Equinox (Mar 20-21), marking equal day and night. Full ACTIVATE threshold—emergence and initiation.',
    aliases: ['SPRING EQUINOX', 'EOSTRE'],
    category: 'sabbatTradition',
    relatedTerms: ['activate', 'spring', 'equinox', 'aries'],
    traditions: 'Egg decorating, planting seeds, balance rituals, spring cleaning completion',
    ritualFocus: 'New beginnings, balance, fertility, emergence from winter',
    natureEvents: 'Equal day/night, spring flowers, bird migration returns, planting season',
    somaticInvitation: 'What are you ready to initiate? What balance are you seeking?'
  },
  beltaneTradition: {
    term: 'BELTANE',
    slug: 'beltane-tradition',
    definition: 'The sabbat halfway between Spring Equinox and Summer Solstice (May 1). Peak ACTIVATE into FULFILL—passion and creative fire.',
    aliases: ['MAY DAY', 'WALPURGIS'],
    category: 'sabbatTradition',
    relatedTerms: ['activate', 'fulfill', 'taurus', 'threshold'],
    traditions: 'Maypole dancing, flower crowns, fire leaping, handfasting, fertility celebrations',
    ritualFocus: 'Passion, creativity, union, celebrating life force, honoring desire',
    natureEvents: 'Peak flowering, maximum growth, pollination, mating seasons',
    somaticInvitation: 'What passion is ready to be expressed? What creative fire burns?'
  },
  lithaTradition: {
    term: 'LITHA (Summer Solstice)',
    slug: 'litha-tradition',
    definition: 'The sabbat of Summer Solstice (Jun 20-21), marking the year\'s longest day. Peak FULFILL—maximum light, abundance, celebration.',
    aliases: ['SUMMER SOLSTICE', 'MIDSUMMER'],
    category: 'sabbatTradition',
    relatedTerms: ['fulfill', 'summer', 'solstice', 'cancer'],
    traditions: 'Bonfires, sun wheels, herb gathering, all-night vigils, honey celebrations',
    ritualFocus: 'Abundance, gratitude, celebration, acknowledging achievements, sun honoring',
    natureEvents: 'Longest day, peak sun strength, abundant growth, first harvests',
    somaticInvitation: 'What have you achieved? What abundance surrounds you? What deserves celebration?'
  },
  lammasTradition: {
    term: 'LAMMAS',
    slug: 'lammas-tradition',
    definition: 'The sabbat halfway between Summer Solstice and Autumn Equinox (Aug 1). First INTEGRATE—first harvest, first letting go.',
    aliases: ['LUGHNASADH', 'FIRST HARVEST'],
    category: 'sabbatTradition',
    relatedTerms: ['fulfill', 'integrate', 'leo', 'threshold'],
    traditions: 'Bread baking, corn dollies, games and contests, grain offerings, first fruits',
    ritualFocus: 'Gratitude for harvest, recognizing what has grown, sacrificing for future growth',
    natureEvents: 'Grain harvest, fruits ripening, days noticeably shorter, late summer heat',
    somaticInvitation: 'What have you harvested this year? What sacrifice supports what comes next?'
  },
  mabonTradition: {
    term: 'MABON (Autumn Equinox)',
    slug: 'mabon-tradition',
    definition: 'The sabbat of Autumn Equinox (Sep 22-23), marking equal day and night. Deep INTEGRATE—harvest completion, gratitude, balance.',
    aliases: ['AUTUMN EQUINOX', 'SECOND HARVEST'],
    category: 'sabbatTradition',
    relatedTerms: ['integrate', 'autumn', 'equinox', 'libra'],
    traditions: 'Harvest festivals, wine making, gratitude rituals, apple magic, balance ceremonies',
    ritualFocus: 'Gratitude, balance, preparing for darker half, completing harvest',
    natureEvents: 'Equal day/night, harvest moon, leaf change beginning, migration',
    somaticInvitation: 'What are you grateful for? What balance do you seek as darkness grows?'
  },
  samhainTradition: {
    term: 'SAMHAIN',
    slug: 'samhain-tradition',
    definition: 'The sabbat halfway between Autumn Equinox and Winter Solstice (Oct 31-Nov 1). Deepest INTEGRATE into NOURISH—honoring death, ancestors, the veil.',
    aliases: ['HALLOWEEN', 'ALL HALLOWS'],
    category: 'sabbatTradition',
    relatedTerms: ['integrate', 'nourish', 'scorpio', 'threshold'],
    traditions: 'Ancestor altars, divination, costumes, bonfires, leaving offerings, silent suppers',
    ritualFocus: 'Honoring the dead, ancestor connection, divination, releasing the old year',
    natureEvents: 'Final harvest, frost, bare trees, thin veil between worlds, end of Celtic year',
    somaticInvitation: 'Who has passed that you wish to honor? What is dying in you that needs acknowledgment?'
  }
};

// =============================================================================
// FAQ CATEGORIES
// =============================================================================

export const FAQ_CATEGORIES = {
  cihBasics: { label: 'Basics', icon: 'compass', color: '#C5A059' },
  cihLogic: { label: 'Logic', icon: 'brain', color: '#4A90E2' },
  seasonsPlace: { label: 'Seasons & Place', icon: 'globe', color: '#7ED321' },
  wheelOfYear: { label: 'Wheel of the Year', icon: 'sun', color: '#F5A623' },
  moonLivedTime: { label: 'Moon & Lived Time', icon: 'moon', color: '#9B59B6' },
  zodiacArchetypes: { label: 'Zodiac & Archetypes', icon: 'star', color: '#FFD700' },
  planetsModulation: { label: 'Planets & Modulation', icon: 'orbit', color: '#00F0FF' },
  safetyScope: { label: 'Safety & Scope', icon: 'shield', color: '#E74C3C' },
  cihTerms: { label: 'Terms', icon: 'book', color: '#1ABC9C' },
  dial: { label: 'The Onescent Dial', icon: 'dial', color: '#C5A059' },
  rewelling: { label: 'ReWelling', icon: 'heart', color: '#E67E22' },
  easterEggs: { label: 'Hidden Gems', icon: 'sparkles', color: '#DDA0DD' },
  // NEW FAQ Categories for v2.23
  bodySystems: { label: 'Body & Somatic', icon: 'heart-pulse', color: '#FF6B6B' },
  archetypesMyth: { label: 'Archetypes & Myth', icon: 'users', color: '#9B59B6' },
  elementsModalities: { label: 'Elements & Modes', icon: 'layers', color: '#2ECC71' },
  sabbatRituals: { label: 'Sabbat Rituals', icon: 'flame', color: '#F39C12' }
};

// =============================================================================
// FAQ ENTRIES (Expanded with Easter Eggs & Conversation Starters)
// =============================================================================

export const FAQ = [
  // CiH Basics
  {
    id: 'cih-001',
    category: 'cihBasics',
    question: 'What is Cycles in Harmony (CiH)?',
    answer: 'Cycles in Harmony is a non-predictive, non-prescriptive cosmology for orientation. It helps us notice repeating rhythms across nature and the sky (seasons, lunar phases, zodiac timing, planetary motion, and cultural seasonal markers). Cycles in Harmony does not tell anyone what to do. It helps us recognize timing, conditions, and patterns so we can choose responses that feel coherent and sustainable.',
    relatedTerms: ['cosmology', 'orientation', 'attunement'],
    conversationStarter: 'What rhythms do you already notice in your own life?'
  },
  {
    id: 'cih-002',
    category: 'cihBasics',
    question: 'Is Cycles in Harmony a belief system or religion?',
    answer: 'No. Cycles in Harmony is not a religion, doctrine, or required belief system. It is a descriptive framework for relationship with time, place, and cyclical change. We can use it as a lens, set it down, revise it, or adapt it without needing agreement or devotion.',
    relatedTerms: ['doctrine', 'dogma', 'boundaries'],
    conversationStarter: 'What frameworks help you make sense of time without demanding belief?'
  },
  {
    id: 'cih-003',
    category: 'cihBasics',
    question: 'What is Cycles in Harmony used for?',
    answer: 'Cycles in Harmony is used for orientation. It can support seasonal living, creative timing, reflection, spiritual practice, and planning with more compassion for natural ebb and flow. It can also help teams and communities name shared rhythms without forcing uniform behavior.',
    relatedTerms: ['orientation', 'practice', 'attunement'],
    conversationStarter: 'If you could align one area of your life with natural rhythms, what would it be?'
  },
  {
    id: 'cih-004',
    category: 'cihBasics',
    question: 'What are the main cycles Cycles in Harmony works with?',
    answer: 'Cycles in Harmony works with five core cycle languages: (1) solar seasons, (2) lunar phases, (3) the zodiac as a seasonal timing wheel, (4) planetary and luminary motion as modulators of emphasis, and (5) seasonal cultural markers such as the Wheel of the Year as a regional interface. Cycles in Harmony treats these as layered and nested rather than separate or competing.',
    relatedTerms: ['sun', 'moon', 'luminaries', 'planets', 'zodiac'],
    conversationStarter: 'Which cycle do you feel most connected to right now?'
  },
  
  // CiH Logic
  {
    id: 'cih-005',
    category: 'cihLogic',
    question: 'What is the "Universal Cycle" in Cycles in Harmony?',
    answer: 'Cycles in Harmony describes a repeating four-phase pattern that shows up across many processes: Nourish → Activate → Fulfill → Integrate. This is not a rule and not a schedule. It is a map for noticing how things tend to unfold through rest, engagement, visibility, and reflection.',
    relatedTerms: ['nourish', 'activate', 'fulfill', 'integrate', 'universal'],
    conversationStarter: 'Which phase feels most natural to you? Which feels most challenging?'
  },
  {
    id: 'cih-006',
    category: 'cihLogic',
    question: 'Does Cycles in Harmony say there are only four phases to everything?',
    answer: 'No. Cycles in Harmony uses a four-phase structure as a simple, practical orientation. Many cycles can be described with more detail. The four-phase model is a stable "backbone" we can use without needing complexity to be meaningful.',
    relatedTerms: ['phase', 'fractal', 'universal'],
    conversationStarter: 'Do you prefer simple frameworks or detailed ones? What does that tell you about your own cognition style?'
  },
  {
    id: 'cih-007',
    category: 'cihLogic',
    question: 'Is Cycles in Harmony predictive astrology?',
    answer: 'No. Cycles in Harmony is not predictive. It does not claim planets "cause" events or guarantee outcomes. It treats planetary cycles as conditions that can shift emphasis, timing, and felt pressure. We keep agency central and avoid deterministic claims.',
    relatedTerms: ['astrology', 'planets', 'boundaries', 'agency'],
    conversationStarter: 'How does it feel to think of celestial patterns as "weather" rather than "fate"?'
  },
  {
    id: 'cih-008',
    category: 'cihLogic',
    question: 'What does Cycles in Harmony mean by "attunement"?',
    answer: 'Attunement is the ongoing practice of calibrating your awareness to present conditions—inner states, relational dynamics, and contextual factors. It\'s not about achieving a perfect state but about staying curious and responsive.',
    relatedTerms: ['attunement', 'awareness', 'presence', 'calibration'],
    conversationStarter: 'What helps you stay attuned when life gets chaotic?'
  },
  {
    id: 'cih-009',
    category: 'cihLogic',
    question: 'Why does CiH avoid the word "should"?',
    answer: 'Cycles in Harmony describes conditions and availability rather than prescriptions. "Should" implies obligation and external authority. CiH language uses "tends to support," "may feel aligned," or "conditions favor" to preserve your agency and choice.',
    relatedTerms: ['agency', 'boundaries', 'ethics'],
    conversationStarter: 'Notice how you feel when someone says "you should" versus "you might consider"—what\'s different?'
  },
  
  // Seasons & Place
  {
    id: 'cih-010',
    category: 'seasonsPlace',
    question: 'Do seasons work differently in the Southern Hemisphere?',
    answer: 'Yes! CiH acknowledges hemispheric inversion. When it\'s summer solstice in the North, it\'s winter solstice in the South. The Onescent Dial automatically adjusts all seasonal interpretations based on your location—no Northern Hemisphere default.',
    relatedTerms: ['inversion', 'hemisphere', 'local', 'latitude'],
    conversationStarter: 'If you\'ve traveled across hemispheres, did you notice your body responding to the different seasonal timing?'
  },
  {
    id: 'cih-011',
    category: 'seasonsPlace',
    question: 'What about people who live near the equator?',
    answer: 'Equatorial regions experience wet/dry seasons rather than the four temperate seasons. The dial switches to show these rhythms, often including ITCZ (monsoon) timing. CiH honors local reality over universal templates.',
    relatedTerms: ['climate', 'local', 'region'],
    conversationStarter: 'What seasonal rhythms does your body actually feel, regardless of what the calendar says?'
  },
  {
    id: 'cih-012',
    category: 'seasonsPlace',
    question: 'Why does location matter for celestial interpretation?',
    answer: 'Your geographic position determines daylight hours, seasonal intensity, climate patterns, and atmospheric conditions—all of which influence how cosmic rhythms are experienced. The same Full Moon feels different in Alaska versus Kenya.',
    relatedTerms: ['latitude', 'longitude', 'local', 'field'],
    conversationStarter: 'How might your relationship with the sky change if you moved to a radically different latitude?'
  },
  
  // Wheel of the Year
  {
    id: 'cih-013',
    category: 'wheelOfYear',
    question: 'What are sabbats?',
    answer: 'Sabbats are eight seasonal markers in the Wheel of the Year tradition, dividing the solar year into segments. They include solstices, equinoxes, and cross-quarter days. In Cycles in Harmony, sabbats provide cultural interface points for seasonal attunement.',
    relatedTerms: ['sabbat', 'season', 'threshold', 'wheelOfYear'],
    conversationStarter: 'Which sabbat (or seasonal threshold) do you feel most drawn to celebrate?'
  },
  {
    id: 'cih-014',
    category: 'wheelOfYear',
    question: 'Are sabbats religious?',
    answer: 'Historically, sabbats have roots in various spiritual traditions. In Cycles in Harmony, they\'re treated as cultural interfaces for seasonal awareness—you can engage with them spiritually, secularly, or not at all. They\'re optional orientation tools.',
    relatedTerms: ['sabbat', 'boundaries', 'practice'],
    conversationStarter: 'What secular rituals do you already have for marking seasonal transitions?'
  },
  
  // Moon & Lived Time
  {
    id: 'cih-015',
    category: 'moonLivedTime',
    question: 'How does the Moon affect daily life in Cycles in Harmony?',
    answer: 'The Moon governs short-arc embodied rhythm. Its ~29.5 day cycle provides a natural month with four phases corresponding to NOURISH (New), ACTIVATE (First Quarter), FULFILL (Full), and INTEGRATE (Last Quarter). Many people notice shifts in energy, mood, or creativity with the lunar cycle.',
    relatedTerms: ['moon', 'lunation', 'phase', 'embodiment'],
    conversationStarter: 'Have you ever tracked your energy or creativity against moon phases? What did you notice?'
  },
  {
    id: 'cih-016',
    category: 'moonLivedTime',
    question: 'What makes eclipses special?',
    answer: 'Eclipses occur when the Sun, Moon, and Earth align precisely, temporarily shifting light patterns. In Cycles in Harmony, eclipses are threshold events—moments of amplified significance that can catalyze change or reveal what\'s been hidden. They\'re not "bad," just intense.',
    relatedTerms: ['eclipse', 'threshold', 'node', 'lunation'],
    conversationStarter: 'What has historically shifted or revealed itself around eclipse times in your life?'
  },
  
  // Zodiac & Archetypes
  {
    id: 'cih-017',
    category: 'zodiacArchetypes',
    question: 'What are zodiac signs in Cycles in Harmony?',
    answer: 'Zodiac signs are twelve archetypal lenses dividing the ecliptic. They describe qualities of timing and emphasis, not personality traits or destiny. The Sun moving through Aries describes a field condition, not a prediction about Aries-Sun people.',
    relatedTerms: ['zodiac', 'sign', 'archetype'],
    conversationStarter: 'How would it feel to think of your "Sun sign" as a timing quality rather than a personality box?'
  },
  {
    id: 'cih-018',
    category: 'zodiacArchetypes',
    question: 'What do elements (Fire, Earth, Air, Water) mean?',
    answer: 'Elements are fundamental qualities expressed through signs. Fire signs emphasize spirit and action; Earth signs emphasize matter and stability; Air signs emphasize mind and connection; Water signs emphasize emotion and depth. In Cycles in Harmony, elements describe mode of expression.',
    relatedTerms: ['element', 'fire', 'earth', 'air', 'water'],
    conversationStarter: 'Which element do you feel most at home in? Which feels foreign?'
  },
  
  // Planets & Modulation
  {
    id: 'cih-019',
    category: 'planetsModulation',
    question: 'What does Mercury retrograde actually mean?',
    answer: 'Mercury retrograde is an optical illusion where Mercury appears to move backward from Earth\'s perspective. In Cycles in Harmony, it signals a time for review, revision, and re-examination of Mercury themes (communication, thought, travel). It\'s not cosmically "bad"—just reflective.',
    relatedTerms: ['retrograde', 'mercury', 'review'],
    conversationStarter: 'What have you needed to review or revisit lately? Could that be Mercury retrograde\'s invitation?'
  },
  {
    id: 'cih-020',
    category: 'planetsModulation',
    question: 'How do planetary aspects work?',
    answer: 'Aspects are angular relationships between planets. Conjunctions (0°) fuse energies; oppositions (180°) create polarity; trines (120°) flow easily; squares (90°) create friction; sextiles (60°) offer openings. In Cycles in Harmony, aspects describe how different archetypal voices interact in the current field.',
    relatedTerms: ['aspect', 'conjunction', 'opposition', 'trine', 'square', 'sextile'],
    conversationStarter: 'Think of two parts of yourself that feel in tension—that\'s a "square" in action. What might the friction be teaching you?'
  },
  
  // Safety & Scope
  {
    id: 'cih-021',
    category: 'safetyScope',
    question: 'Is CiH safe?',
    answer: 'CiH is designed with safety in mind. It never prescribes action, never overrides agency, and always includes safeguard language. However, any framework engaging with meaning and timing can activate strong feelings. If you\'re in crisis, please seek professional support.',
    relatedTerms: ['safety', 'boundaries', 'agency'],
    conversationStarter: 'What would it look like to engage with cyclical awareness while honoring your capacity limits?'
  },
  {
    id: 'cih-022',
    category: 'safetyScope',
    question: 'Can CiH replace therapy?',
    answer: 'No. CiH is an orientation framework, not a therapeutic modality. It can complement wellness practices but should never substitute for professional mental health care when needed.',
    relatedTerms: ['boundaries', 'wellness', 'safety'],
    constraints: ['Cycles in Harmony is not therapy.'],
    conversationStarter: 'How do you distinguish between self-reflection tools and professional support needs?'
  },
  {
    id: 'cih-023',
    category: 'safetyScope',
    question: 'What if I don\'t believe in astrology?',
    answer: 'That\'s completely valid. CiH doesn\'t require belief. You can use it as a seasonal awareness tool, a creativity framework, or a philosophical lens without accepting any metaphysical claims. Take what serves you, leave what doesn\'t.',
    relatedTerms: ['boundaries', 'agency', 'inquiry'],
    conversationStarter: 'What frameworks do you find useful even if you don\'t fully "believe" in them?'
  },
  
  // The Onescent Dial
  {
    id: 'cih-024',
    category: 'dial',
    question: 'What is The Onescent Dial?',
    answer: 'The Onescent Dial is a contemporary digital orientation instrument that visualizes CiH principles. It shows real-time celestial positions, phase conditions, and location-aware seasonal data—all interpreted through the non-predictive CiH framework.',
    relatedTerms: ['orientation', 'synthesis', 'field'],
    conversationStarter: 'If you could have a "dashboard" for any aspect of life, what would you want it to show?'
  },
  {
    id: 'cih-025',
    category: 'dial',
    question: 'Why is it called "Onescent"?',
    answer: 'The name plays with "oneness" and "scent"—suggesting both unity and subtle perception. The Dial helps us "catch the scent" of current conditions, orienting to the unified field through gentle, perceptual awareness.',
    relatedTerms: ['attunement', 'awareness', 'unity'],
    conversationStarter: 'What would it mean to "catch the scent" of the current moment right now?'
  },
  {
    id: 'cih-026',
    category: 'dial',
    question: 'Can I export or share my dial reading?',
    answer: 'Yes! The dial supports PNG, PDF, and link sharing. You can capture a moment\'s conditions and share them with others—perfect for group planning, reflection documentation, or simply marking significant times.',
    relatedTerms: ['synthesis', 'sharing'],
    conversationStarter: 'What moment would you want to capture and revisit later?'
  },
  
  // Easter Eggs & Hidden Gems
  {
    id: 'cih-027',
    category: 'easterEggs',
    question: 'What\'s the significance of the four-phase pattern appearing everywhere?',
    answer: 'The NOURISH → ACTIVATE → FULFILL → INTEGRATE pattern is fractal—it appears in breaths (inhale, hold, exhale, pause), days (night, morning, noon, evening), months (new moon, waxing, full, waning), years (winter, spring, summer, autumn), and beyond. It\'s not that we\'re forcing a pattern onto reality; it\'s that this pattern emerges naturally from how cycles work.',
    relatedTerms: ['fractal', 'universal', 'cycle'],
    conversationStarter: 'Where else do you notice this four-fold pattern in your life?',
    isEasterEgg: true
  },
  {
    id: 'cih-028',
    category: 'easterEggs',
    question: 'Why do some moments feel "thicker" than others?',
    answer: 'In Cycles in Harmony, we\'d say moments have different "field density." When multiple cycles converge—eclipse, retrograde, sabbat, personal anniversary—the field thickens with meaning potential. This isn\'t magical thinking; it\'s pattern recognition meeting heightened attention.',
    relatedTerms: ['field', 'threshold', 'convergence'],
    conversationStarter: 'What makes a moment feel significant to you?',
    isEasterEgg: true
  },
  {
    id: 'cih-029',
    category: 'easterEggs',
    question: 'Is there a connection between quantum physics and astrology?',
    answer: 'Cycles in Harmony uses quantum concepts (entanglement, non-locality, observer effect) as metaphors, not proofs. Both domains deal with relationship, observation, and interconnection—but we\'re careful not to claim scientific validation. It\'s parallel poetry, not proof.',
    relatedTerms: ['quantum', 'metaphor', 'physics'],
    conversationStarter: 'What happens when you hold something as "useful metaphor" rather than "literal truth"?',
    isEasterEgg: true
  },
  {
    id: 'cih-030',
    category: 'easterEggs',
    question: 'What\'s the deal with the "Crystalline Web" metaphor?',
    answer: 'The Crystalline Web is CiH\'s visualization of reality as an interconnected network where each node (person, moment, place) refracts the whole in unique ways. It\'s like Indra\'s Net from Buddhist philosophy—every jewel reflects every other. You\'re both a unique facet and inseparable from the whole.',
    relatedTerms: ['crystallineWeb', 'relationship', 'unity'],
    conversationStarter: 'If you\'re a facet of a crystalline web, what unique angle do you refract?',
    isEasterEgg: true
  },
  {
    id: 'cih-031',
    category: 'easterEggs',
    question: 'Why does the dial adjust for location automatically?',
    answer: 'Because universal templates are a Northern Hemisphere bias problem. For too long, seasonal and celestial frameworks assumed everyone lived in temperate northern latitudes. The dial\'s location awareness is a design commitment to genuine universality through honoring particularity.',
    relatedTerms: ['local', 'global', 'inversion', 'latitude'],
    conversationStarter: 'What other "universal" systems have you noticed are secretly parochial?',
    isEasterEgg: true
  },
  {
    id: 'cih-032',
    category: 'easterEggs',
    question: 'Can I use CiH to time important decisions?',
    answer: 'You can use it as one input among many. CiH might suggest "this phase supports reflection" or "current conditions favor action"—but the choice is always yours. Timing wisdom comes from integrating cyclical awareness with your own discernment, not from outsourcing decisions to the sky.',
    relatedTerms: ['agency', 'timing', 'orientation'],
    conversationStarter: 'How do you balance external timing signals with your own inner knowing?',
    isEasterEgg: true
  },
  {
    id: 'cih-033',
    category: 'easterEggs',
    question: 'What\'s the most common mistake people make with astrology?',
    answer: 'Treating it as prediction rather than orientation. "Mercury retrograde will cause your car to break down" is prediction. "Mercury retrograde invites review of how you communicate and travel" is orientation. One steals agency; the other enhances it.',
    relatedTerms: ['agency', 'astrology', 'orientation'],
    conversationStarter: 'Where have you felt your agency diminished by deterministic frameworks?',
    isEasterEgg: true
  },
  {
    id: 'cih-034',
    category: 'easterEggs',
    question: 'Is there a "best" time to do anything?',
    answer: 'In Cycles in Harmony, there\'s no universally "best" time—only more or less aligned timing for specific activities in specific contexts. What\'s aligned for you depends on your capacity, the activity, your relationships, and the broader field. Timing wisdom is relational, not absolute.',
    relatedTerms: ['timing', 'context', 'capacity'],
    conversationStarter: 'What would change if you stopped searching for the "best" time and started noticing "aligned-enough" times?',
    isEasterEgg: true
  },
  {
    id: 'cih-035',
    category: 'easterEggs',
    question: 'Why does CiH insist on "non-prescriptive" language?',
    answer: 'Because prescription creates dependency and hierarchy. If we tell you what to do, we become authorities over your life. Cycles in Harmony describes conditions so you can make informed choices. We trust you to know yourself better than any framework ever could.',
    relatedTerms: ['agency', 'boundaries', 'ethics'],
    conversationStarter: 'How does it feel to be trusted with your own interpretation?',
    isEasterEgg: true
  },
  
  // NEW: SEO-Optimized Questions for Global Positioning
  {
    id: 'cih-036',
    category: 'seasonsPlace',
    question: 'How does The Onescent Dial work for different time zones?',
    answer: 'The dial automatically adjusts all calculations to your exact longitude, ensuring solar noon, moonrise times, and all celestial positions are accurate for your specific location. This spacio-temporal precision is what makes the dial a truly global orientation instrument.',
    relatedTerms: ['longitude', 'local', 'global', 'field'],
    conversationStarter: 'Have you noticed how your relationship with time changes when traveling across time zones?'
  },
  {
    id: 'cih-037',
    category: 'seasonsPlace',
    question: 'What is Spacio-Temporal Analysis™?',
    answer: 'Spacio-Temporal Analysis™ is our proprietary feature for comparing how the same moment manifests across different locations. It reveals how geographic position fundamentally shapes the experience of celestial conditions—making visible the "local" in universal rhythms.',
    relatedTerms: ['local', 'global', 'latitude', 'longitude', 'field'],
    conversationStarter: 'If you could experience the same moment from two different places on Earth, where would you choose?'
  },
  {
    id: 'cih-038',
    category: 'dial',
    question: 'Can The Onescent Dial show historical or future dates?',
    answer: 'Yes! You can navigate to any date to see the celestial conditions of that moment. This is powerful for reflection (understanding past significant moments) and planning (exploring upcoming field conditions). The dial becomes a time-travel tool for orientation.',
    relatedTerms: ['temporal', 'transit', 'timing'],
    conversationStarter: 'What past moment would you like to revisit through the lens of its celestial conditions?'
  },
  {
    id: 'cih-039',
    category: 'dial',
    question: 'How does comparative analysis work?',
    answer: 'Comparative analysis allows you to view two charts side-by-side: transit to natal (how current sky affects your birth chart), synastry (relationship dynamics between two people), or spacio-temporal comparison (same moment, different places). Meaning emerges from the interplay.',
    relatedTerms: ['duality', 'synthesis', 'relationship', 'modulation'],
    conversationStarter: 'What relationship or comparison are you most curious to explore?'
  },
  {
    id: 'cih-040',
    category: 'easterEggs',
    question: 'What makes a cosmology "good"?',
    answer: 'A good cosmology is useful without being coercive. It helps you orient without claiming to predict. It acknowledges its own limitations. It serves diverse contexts without demanding uniform interpretation. CiH aims to be a good cosmology—descriptive, revisable, respectful of agency.',
    relatedTerms: ['cosmology', 'ethics', 'boundaries', 'agency'],
    conversationStarter: 'What qualities would your ideal orientation framework have?',
    isEasterEgg: true
  },
  
  // =========================================================================
  // BODY SYSTEMS & SOMATIC (NEW)
  // =========================================================================
  {
    id: 'body-001',
    category: 'bodySystems',
    question: 'What are body correspondences in Cycles in Harmony?',
    answer: 'Body correspondences are traditional associations linking celestial bodies and zodiac signs to parts of the physical body. In Cycles in Harmony, these are awareness tools—not medical claims. They invite somatic check-ins: noticing how different cosmic timing may resonate with different parts of your embodied experience.',
    relatedTerms: ['body-correspondence', 'melothesia', 'embodiment'],
    conversationStarter: 'When you think of the Moon, what part of your body do you notice?'
  },
  {
    id: 'body-002',
    category: 'bodySystems',
    question: 'Can body correspondences diagnose health issues?',
    answer: 'Absolutely not. Body correspondences are symbolic and traditional—they have no medical validity. If you have health concerns, consult healthcare professionals. CiH body awareness is about noticing and reflecting, never diagnosing or prescribing.',
    relatedTerms: ['boundaries', 'safety', 'wellness'],
    constraints: ['Never medical advice.'],
    conversationStarter: 'How do you distinguish between somatic awareness and health anxiety?'
  },
  {
    id: 'body-003',
    category: 'bodySystems',
    question: 'How do I work with body correspondences practically?',
    answer: 'Use them as prompts for body scans. When Mars is active, check in with your head, muscles, and energy levels. When Moon is in Cancer, notice your chest, stomach, and emotional state. The goal is embodied awareness, not prediction.',
    relatedTerms: ['practice', 'embodiment', 'attunement'],
    conversationStarter: 'What simple body check-in could you add to your daily rhythm?'
  },
  {
    id: 'body-004',
    category: 'bodySystems',
    question: 'What is melothesia?',
    answer: 'Melothesia is the ancient practice of mapping zodiac signs to body parts, creating the "zodiacal human" (homo signorum). From Aries ruling the head to Pisces ruling the feet, this system represents the body as a microcosm of cosmic order. In Cycles in Harmony, it\'s a poetic framework for embodied reflection.',
    relatedTerms: ['melothesia', 'zodiac', 'archetype'],
    conversationStarter: 'If your body were a zodiac wheel, which sign\'s territory feels most active today?'
  },

  // =========================================================================
  // ARCHETYPES & MYTHOLOGY (NEW)
  // =========================================================================
  {
    id: 'arch-001',
    category: 'archetypesMyth',
    question: 'What is an archetype in Cycles in Harmony?',
    answer: 'An archetype is a universal pattern of meaning that appears across cultures and times. In Cycles in Harmony, archetypes associated with planets and signs are lenses for understanding—not fixed identities. You are not "a Warrior" or "a Nurturer"—you have access to all archetypal patterns in different contexts.',
    relatedTerms: ['archetype', 'mythology', 'meaning'],
    conversationStarter: 'Which archetypal pattern feels most alive in you this week?'
  },
  {
    id: 'arch-002',
    category: 'archetypesMyth',
    question: 'How do archetypes relate to planets?',
    answer: 'Each planet embodies an archetypal principle: Mars expresses the Warrior, Venus the Lover, Saturn the Elder, Neptune the Mystic, and so on. These associations come from millennia of mythological and astrological tradition. In Cycles in Harmony, they help us name different qualities of experience.',
    relatedTerms: ['mars', 'venus', 'saturn', 'neptune', 'mythology'],
    conversationStarter: 'Which planetary archetype do you feel most comfortable embodying? Which feels foreign?'
  },
  {
    id: 'arch-003',
    category: 'archetypesMyth',
    question: 'What is shadow expression of an archetype?',
    answer: 'Every archetype has healthy and shadow expressions. The Warrior can be courageous (healthy) or aggressive (shadow). The Nurturer can be caring (healthy) or smothering (shadow). In Cycles in Harmony, recognizing shadow expressions helps us stay in balanced relationship with archetypal energies.',
    relatedTerms: ['archetype', 'balance', 'wellness'],
    conversationStarter: 'Which archetype\'s shadow do you most need to watch in yourself?'
  },
  {
    id: 'arch-004',
    category: 'archetypesMyth',
    question: 'Why do planets have mythological names?',
    answer: 'The planets were named after deities because ancient observers noticed their light, motion, and character seemed to embody certain qualities. Mars glows red (blood, war), Venus is brilliant at dawn/dusk (beauty, love), Saturn moves slowly (time, age). These names carry millennia of human meaning-making.',
    relatedTerms: ['mythology', 'planets', 'meaning'],
    conversationStarter: 'If you could rename a planet based on your experience of it, what would you call it?'
  },

  // =========================================================================
  // ELEMENTS & MODALITIES (NEW)
  // =========================================================================
  {
    id: 'elem-001',
    category: 'elementsModalities',
    question: 'What are the four elements in Cycles in Harmony?',
    answer: 'Fire, Earth, Air, and Water are the four elemental qualities. Fire expresses through spirit and action; Earth through matter and stability; Air through mind and connection; Water through emotion and depth. In Cycles in Harmony, elements describe modes of expression—how energy moves and manifests.',
    relatedTerms: ['fire-element', 'earth-element', 'air-element', 'water-element'],
    conversationStarter: 'Which element do you feel most nourished by? Which depletes you?'
  },
  {
    id: 'elem-002',
    category: 'elementsModalities',
    question: 'What are the three modalities?',
    answer: 'Cardinal, Fixed, and Mutable are the three modalities describing HOW signs engage with change. Cardinal signs (Aries, Cancer, Libra, Capricorn) initiate. Fixed signs (Taurus, Leo, Scorpio, Aquarius) sustain. Mutable signs (Gemini, Virgo, Sagittarius, Pisces) adapt.',
    relatedTerms: ['cardinal-mode', 'fixed-mode', 'mutable-mode'],
    conversationStarter: 'When facing change, do you tend to initiate, sustain, or adapt first?'
  },
  {
    id: 'elem-003',
    category: 'elementsModalities',
    question: 'How do elements and modes combine?',
    answer: 'Each zodiac sign has both an element and a mode: Aries is Cardinal Fire, Taurus is Fixed Earth, and so on. This creates 12 unique combinations. The element tells us WHAT kind of energy; the mode tells us HOW it engages. Cardinal Fire (Aries) initiates with spirit; Fixed Water (Scorpio) sustains emotion.',
    relatedTerms: ['zodiac', 'element', 'modality'],
    conversationStarter: 'What element-mode combination best describes how you approach your work?'
  },
  {
    id: 'elem-004',
    category: 'elementsModalities',
    question: 'Why do elements relate to CiH phases?',
    answer: 'Elements have natural phase affinities. Fire aligns with ACTIVATE (initiative, action). Earth aligns with NOURISH (stability, grounding). Air bridges ACTIVATE and FULFILL (expression, connection). Water aligns with NOURISH and INTEGRATE (feeling, release). These correspondences enrich phase understanding.',
    relatedTerms: ['activate', 'nourish', 'fulfill', 'integrate'],
    conversationStarter: 'Which phase-element pairing feels most like home to you?'
  },

  // =========================================================================
  // SABBAT RITUALS & TRADITIONS (NEW)
  // =========================================================================
  {
    id: 'sab-001',
    category: 'sabbatRituals',
    question: 'What rituals are traditional for each sabbat?',
    answer: 'Each sabbat has associated practices: Yule (candles, Yule log, gifts), Imbolc (spring cleaning, Brigid\'s crosses), Ostara (egg decorating, planting), Beltane (Maypole, flower crowns), Litha (bonfires, herb gathering), Lammas (bread baking), Mabon (harvest festivals), Samhain (ancestor altars, divination). In Cycles in Harmony, these are optional cultural interfaces.',
    relatedTerms: ['yule-tradition', 'imbolc-tradition', 'ostara-tradition', 'beltane-tradition', 'litha-tradition', 'lammas-tradition', 'mabon-tradition', 'samhain-tradition'],
    conversationStarter: 'Which sabbat tradition most appeals to you, and why?'
  },
  {
    id: 'sab-002',
    category: 'sabbatRituals',
    question: 'Can I celebrate sabbats without being religious?',
    answer: 'Absolutely. While sabbats have roots in various spiritual traditions, Cycles in Harmony treats them as secular seasonal markers. You can bake bread at Lammas, clean house at Imbolc, or honor ancestors at Samhain without any religious framework. They\'re cultural technologies for seasonal awareness.',
    relatedTerms: ['boundaries', 'practice', 'wheelOfYear'],
    conversationStarter: 'What secular ritual could you create to mark the next seasonal threshold?'
  },
  {
    id: 'sab-003',
    category: 'sabbatRituals',
    question: 'What nature events correspond to sabbats?',
    answer: 'Each sabbat corresponds to observable nature: Yule (longest night, dormancy), Imbolc (first stirrings, snowdrops), Ostara (spring flowers, returning birds), Beltane (peak flowering), Litha (longest day, abundance), Lammas (grain harvest), Mabon (harvest moon, migration), Samhain (final harvest, frost).',
    relatedTerms: ['season', 'threshold', 'field'],
    conversationStarter: 'What nature sign will you watch for at the next seasonal threshold?'
  },
  {
    id: 'sab-004',
    category: 'sabbatRituals',
    question: 'How do sabbats relate to CiH phases?',
    answer: 'Sabbats map to CiH phases: Yule and Imbolc are NOURISH (gestation, first stirrings). Ostara and Beltane are ACTIVATE (emergence, passion). Litha and Lammas are FULFILL (abundance, harvest). Mabon and Samhain are INTEGRATE (gratitude, release). This creates a phase-aware seasonal calendar.',
    relatedTerms: ['nourish', 'activate', 'fulfill', 'integrate', 'wheelOfYear'],
    conversationStarter: 'Which sabbat-phase pairing resonates most with your current life stage?'
  },
  
  // =========================================================================
  // OFFICIAL RESOURCES (NEW)
  // =========================================================================
  {
    id: 'res-001',
    category: 'cihBasics',
    question: 'Where can I learn more about Cycles in Harmony?',
    answer: 'For deeper exploration of CiH principles, visit Onesce Press (onescepress.com) for official resources and publications. The free online periodical Onescence (onescence.com) offers regular articles and insights. CiH is developed by mayryanna under Optimal Mastery LLC (optimalmastery.life).',
    relatedTerms: ['cih', 'orientation', 'practice'],
    conversationStarter: 'What aspect of CiH would you like to explore more deeply?',
    brandLinks: {
      onescePress: 'https://onescepress.com',
      onescence: 'https://onescence.com',
      mayryanna: 'https://mayryanna.link',
      optimalMastery: 'https://optimalmastery.life'
    }
  },
  {
    id: 'res-002',
    category: 'cihBasics',
    question: 'Who created Cycles in Harmony?',
    answer: 'Cycles in Harmony (CiH) was developed by mayryanna as an interpretive framework for cyclical awareness. It is proprietary to Optimal Mastery LLC and the Onesce family of applied wellness brands. CiH draws from astronomical observation, traditional correspondences, and non-prescriptive orientation principles.',
    relatedTerms: ['cih', 'orientation', 'safety'],
    conversationStarter: 'How did you discover CiH?',
    brandLinks: {
      mayryanna: 'https://mayryanna.link',
      optimalMastery: 'https://optimalmastery.life'
    }
  },
  {
    id: 'res-003',
    category: 'cihBasics',
    question: 'Is there a CiH periodical or newsletter?',
    answer: 'Yes! Onescence (onescence.com) is the free online periodical for CiH and cyclical awareness content. It features articles, seasonal reflections, and practical applications of orientation principles. For deeper study resources and publications, visit Onesce Press (onescepress.com).',
    relatedTerms: ['cih', 'practice', 'community'],
    conversationStarter: 'What kind of cyclical awareness content interests you most?',
    brandLinks: {
      onescence: 'https://onescence.com',
      onescePress: 'https://onescepress.com'
    }
  }
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get a term by slug
 */
export const getTerm = (slug) => {
  if (!slug) return null;
  const normalizedSlug = slug.toLowerCase().replace(/\s+/g, '-');
  return GLOSSARY[normalizedSlug] || Object.values(GLOSSARY).find(t => 
    t.slug === normalizedSlug || 
    t.term.toLowerCase() === slug.toLowerCase() ||
    t.aliases?.some(a => a.toLowerCase() === slug.toLowerCase())
  );
};

/**
 * Search glossary by query
 */
export const searchGlossary = (query) => {
  if (!query || query.length < 2) return [];
  const q = query.toLowerCase();
  return Object.values(GLOSSARY).filter(t =>
    t.term.toLowerCase().includes(q) ||
    t.definition.toLowerCase().includes(q) ||
    t.aliases?.some(a => a.toLowerCase().includes(q)) ||
    t.relatedTerms?.some(r => r.toLowerCase().includes(q))
  );
};

/**
 * Get terms by category
 */
export const getTermsByCategory = (category) => {
  return Object.values(GLOSSARY).filter(t => t.category === category);
};

/**
 * Search FAQ by query
 */
export const searchFAQ = (query) => {
  if (!query || query.length < 2) return [];
  const q = query.toLowerCase();
  return FAQ.filter(f =>
    f.question.toLowerCase().includes(q) ||
    f.answer.toLowerCase().includes(q)
  );
};

/**
 * Get FAQs by category
 */
export const getFAQsByCategory = (category) => {
  return FAQ.filter(f => f.category === category);
};

/**
 * Get related FAQs for a term
 */
export const getRelatedFAQs = (termSlug) => {
  return FAQ.filter(f => f.relatedTerms?.includes(termSlug));
};

/**
 * Get related terms
 */
export const getRelatedTerms = (termSlug) => {
  const term = getTerm(termSlug);
  if (!term?.relatedTerms) return [];
  return term.relatedTerms.map(getTerm).filter(Boolean);
};

/**
 * Get all glyphs/symbols from glossary
 */
export const getGlyphs = () => {
  return Object.values(GLOSSARY)
    .filter(t => t.glyph)
    .reduce((acc, t) => ({ ...acc, [t.slug]: t.glyph }), {});
};

/**
 * Get all terms with glyphs
 */
export const getTermsWithGlyphs = () => {
  return Object.values(GLOSSARY).filter(t => t.glyph);
};

/**
 * Get Easter Egg FAQs
 */
export const getEasterEggFAQs = () => {
  return FAQ.filter(f => f.isEasterEgg);
};

/**
 * Get conversation starters
 */
export const getConversationStarters = () => {
  return FAQ.filter(f => f.conversationStarter).map(f => ({
    question: f.question,
    starter: f.conversationStarter
  }));
};

/**
 * Get random Easter Egg
 */
export const getRandomEasterEgg = () => {
  const eggs = getEasterEggFAQs();
  return eggs[Math.floor(Math.random() * eggs.length)];
};

/**
 * Get term count
 */
export const getTermCount = () => Object.keys(GLOSSARY).length;

/**
 * Get FAQ count
 */
export const getFAQCount = () => FAQ.length;

/**
 * Get FAQ by ID
 */
export const getFAQ = (id) => {
  return FAQ.find(f => f.id === id);
};
